import { z } from "zod";
import { bookProductionArtifactBindingV1Schema, bookProductionProductSchema, bookProductionVersionBindingV1Schema, } from "./book-production-evidence.js";
import { bookGenerationSourceBindingV1Schema } from "./book-generation.js";
import { bookPublishingAssetUseExactBindingV1Schema } from "./book-publishing-asset.js";
import { localeSchema } from "./localization-accessibility.js";
import { bookVisualFieldPresentationSchema, bookVisualFieldSourceClassSchema, bookVisualIntentAxisSchema, bookVisualTokenClassSchema, } from "./book-visual-implementation.js";
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const opaqueIdSchema = z.string().trim().min(1).max(240);
const safeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(160)
    .regex(/^[A-Z][A-Z0-9_.]*$/u);
const timestampSchema = z.string().datetime({ offset: true });
const coordinateSchema = z.number().int().min(-40_000_000).max(40_000_000);
const positiveDistanceSchema = z.number().int().positive().max(40_000_000);
const insetSchema = z.number().int().nonnegative().max(1_000_000);
function unique(values, context, path, label) {
    if (new Set(values).size !== values.length)
        context.addIssue({ code: "custom", path, message: `${label} must be unique.` });
}
function exactSet(actual, expected, context, path, label) {
    unique(actual, context, path, label);
    const expectedSet = new Set(expected);
    if (actual.length !== expectedSet.size || actual.some((value) => !expectedSet.has(value)))
        context.addIssue({
            code: "custom",
            path,
            message: `${label} must exactly cover the required identities.`,
        });
}
function exactDirectionFlagKey(value) {
    return JSON.stringify({
        ...value,
        effects: [...value.effects]
            .map((effect) => ({
            ...effect,
            intentSignalIds: [...effect.intentSignalIds].sort(),
        }))
            .sort((left, right) => left.targetProperty.localeCompare(right.targetProperty)),
    });
}
function resolvedVisualPropertyScopeKey(value) {
    return `${value.primitiveField}:${value.primitiveCommandId ?? "PLACEMENT"}`;
}
export const bookOutputGeometryContractSchema = z.literal("BOOK_OUTPUT_GEOMETRY_V1");
export const bookFontRegistryContractSchema = z.literal("BOOK_FONT_REGISTRY_V1");
export const bookPageGrammarCatalogContractSchema = z.literal("BOOK_PAGE_GRAMMAR_CATALOG_V1");
export const bookPageCompositionRequestContractSchema = z.literal("BOOK_PAGE_COMPOSITION_REQUEST_V1");
export const bookPageCompositionPlanContractSchema = z.literal("BOOK_PAGE_COMPOSITION_PLAN_V1");
export const bookDrawLedgerContractSchema = z.literal("BOOK_DRAW_LEDGER_V1");
export const bookPageConstraintObservationV1Schema = z
    .object({
    constraintId: opaqueIdSchema,
    enforcement: z.enum(["SCHEMA", "RENDER_BLOCK", "QA_BLOCK", "HUMAN_REVIEW"]),
    policyBinding: bookProductionVersionBindingV1Schema,
    intentSignalIds: z.array(opaqueIdSchema).min(1).max(100),
    adapterBinding: bookProductionVersionBindingV1Schema,
    disposition: z.enum(["BLOCKED_ADAPTER_REQUIRED", "CARRIED_TO_QA", "CARRIED_TO_HUMAN_REVIEW"]),
    observationDigestSha256: sha256Schema,
})
    .strict()
    .superRefine((value, context) => {
    unique(value.intentSignalIds, context, ["intentSignalIds"], "Constraint intent signals");
    const expected = value.enforcement === "SCHEMA" || value.enforcement === "RENDER_BLOCK"
        ? "BLOCKED_ADAPTER_REQUIRED"
        : value.enforcement === "QA_BLOCK"
            ? "CARRIED_TO_QA"
            : "CARRIED_TO_HUMAN_REVIEW";
    if (value.disposition !== expected)
        context.addIssue({
            code: "custom",
            path: ["disposition"],
            message: "Constraint disposition must preserve its exact enforcement boundary.",
        });
});
export const bookPageRectV1Schema = z
    .object({
    xMilliPoints: coordinateSchema,
    yMilliPoints: coordinateSchema,
    widthMilliPoints: positiveDistanceSchema,
    heightMilliPoints: positiveDistanceSchema,
})
    .strict();
const outputGeometryBodySchema = z
    .object({
    contract: bookOutputGeometryContractSchema,
    geometryId: opaqueIdSchema,
    product: bookProductionProductSchema,
    outputProfileBinding: bookProductionVersionBindingV1Schema,
    renderStageNodeId: opaqueIdSchema,
    stageGraphBinding: bookProductionVersionBindingV1Schema,
    productPolicyBinding: bookProductionVersionBindingV1Schema,
    renderExecutionMode: z.literal("DETERMINISTIC"),
    outputArtifactRole: safeCodeSchema,
    outputContentType: z.literal("application/pdf"),
    coordinateSystem: z
        .object({
        unit: z.literal("MILLI_POINT"),
        origin: z.literal("BLEED_BOX_TOP_LEFT"),
        xAxis: z.literal("RIGHT"),
        yAxis: z.literal("DOWN"),
        rectangleConvention: z.literal("HALF_OPEN"),
    })
        .strict(),
    trim: z
        .object({
        widthMilliPoints: positiveDistanceSchema,
        heightMilliPoints: positiveDistanceSchema,
    })
        .strict(),
    bleedMilliPoints: z
        .object({ top: insetSchema, right: insetSchema, bottom: insetSchema, left: insetSchema })
        .strict(),
    safeInsetsMilliPoints: z
        .object({ top: insetSchema, bottom: insetSchema, outer: insetSchema, inner: insetSchema })
        .strict(),
    readingDirection: z.enum(["LEFT_TO_RIGHT", "RIGHT_TO_LEFT"]),
    firstInteriorPageSide: z.enum(["VERSO", "RECTO"]),
    bindingExclusionRects: z
        .object({ verso: bookPageRectV1Schema.nullable(), recto: bookPageRectV1Schema.nullable() })
        .strict(),
    minimumPlacedDpi: z.number().finite().positive().max(2_400),
    pageCountPolicy: z
        .object({
        minimum: z.number().int().positive().max(2_000),
        maximum: z.number().int().positive().max(2_000),
        multipleOf: z.number().int().positive().max(64),
    })
        .strict(),
    truthBoundary: z.literal("SUPPLIED_OUTPUT_PROFILE_NOT_AUTHENTICATED"),
})
    .strict()
    .superRefine((value, context) => {
    if (value.pageCountPolicy.minimum > value.pageCountPolicy.maximum)
        context.addIssue({
            code: "custom",
            path: ["pageCountPolicy"],
            message: "Page-count minimum cannot exceed maximum.",
        });
    if (value.safeInsetsMilliPoints.top + value.safeInsetsMilliPoints.bottom >=
        value.trim.heightMilliPoints ||
        value.safeInsetsMilliPoints.outer + value.safeInsetsMilliPoints.inner >=
            value.trim.widthMilliPoints)
        context.addIssue({
            code: "custom",
            path: ["safeInsetsMilliPoints"],
            message: "Safe insets must leave a positive live-content area.",
        });
});
export const bookOutputGeometryV1Schema = outputGeometryBodySchema.safeExtend({
    geometryDigestSha256: sha256Schema,
});
export const bookUnicodeRangeV1Schema = z
    .object({
    startCodePoint: z.number().int().nonnegative().max(0x10ffff),
    endCodePoint: z.number().int().nonnegative().max(0x10ffff),
})
    .strict()
    .refine(({ startCodePoint, endCodePoint }) => startCodePoint <= endCodePoint, "Unicode ranges must be ordered.");
export const bookFontEntryV1Schema = z
    .object({
    fontId: opaqueIdSchema,
    familyName: z.string().trim().min(1).max(200),
    faceName: z.string().trim().min(1).max(200),
    fontBinding: bookProductionVersionBindingV1Schema,
    governedAssetUseBinding: bookPublishingAssetUseExactBindingV1Schema,
    embeddingPolicy: z.enum(["FULL", "SUBSET"]),
    supportedScripts: z.array(safeCodeSchema).min(1).max(80),
    supportedDirections: z
        .array(z.enum(["LEFT_TO_RIGHT", "RIGHT_TO_LEFT", "TOP_TO_BOTTOM"]))
        .min(1)
        .max(3),
    unicodeRanges: z.array(bookUnicodeRangeV1Schema).min(1).max(300),
    shaping: z
        .object({
        requiredScripts: z.array(safeCodeSchema).max(80),
        engineBinding: bookProductionVersionBindingV1Schema.nullable(),
    })
        .strict(),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.supportedScripts, context, ["supportedScripts"], "Supported scripts");
    unique(value.supportedDirections, context, ["supportedDirections"], "Supported directions");
    unique(value.shaping.requiredScripts, context, ["shaping", "requiredScripts"], "Shaping-required scripts");
    for (const script of value.shaping.requiredScripts)
        if (!value.supportedScripts.includes(script))
            context.addIssue({
                code: "custom",
                path: ["shaping", "requiredScripts"],
                message: `Shaping-required script ${script} is not supported.`,
            });
    if (value.shaping.requiredScripts.length > 0 && !value.shaping.engineBinding)
        context.addIssue({
            code: "custom",
            path: ["shaping", "engineBinding"],
            message: "Shaping-required fonts require an exact shaping-engine binding.",
        });
});
export const bookFontFallbackChainV1Schema = z
    .object({
    fallbackChainId: opaqueIdSchema,
    script: safeCodeSchema,
    languageTags: z.array(localeSchema).min(1).max(100),
    direction: z.enum(["LEFT_TO_RIGHT", "RIGHT_TO_LEFT", "TOP_TO_BOTTOM"]),
    fontIds: z.array(opaqueIdSchema).min(1).max(20),
    shapingEngineBinding: bookProductionVersionBindingV1Schema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.languageTags, context, ["languageTags"], "Fallback languages");
    unique(value.fontIds, context, ["fontIds"], "Fallback font IDs");
});
const fontRegistryBodySchema = z
    .object({
    contract: bookFontRegistryContractSchema,
    fontRegistryId: opaqueIdSchema,
    product: bookProductionProductSchema,
    registryBinding: bookProductionVersionBindingV1Schema,
    normalizationPolicy: z.literal("NFC"),
    normalizationPolicyBinding: bookProductionVersionBindingV1Schema,
    measurementEngineBinding: bookProductionVersionBindingV1Schema,
    measurementConfigDigestSha256: sha256Schema,
    segmentationEngineBinding: bookProductionVersionBindingV1Schema,
    segmentationConfigDigestSha256: sha256Schema,
    bidiEngineBinding: bookProductionVersionBindingV1Schema,
    bidiConfigDigestSha256: sha256Schema,
    fonts: z.array(bookFontEntryV1Schema).min(1).max(300),
    fallbackChains: z.array(bookFontFallbackChainV1Schema).min(1).max(300),
    truthBoundary: z.literal("SUPPLIED_FONT_AND_ASSET_USE_RECORDS_NOT_AUTHENTICATED"),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.fonts.map(({ fontId }) => fontId), context, ["fonts"], "Font IDs");
    unique(value.fonts.map(({ governedAssetUseBinding }) => governedAssetUseBinding.useId), context, ["fonts"], "Governed font asset-use IDs");
    unique(value.fallbackChains.map(({ fallbackChainId }) => fallbackChainId), context, ["fallbackChains"], "Fallback-chain IDs");
    const fontIds = new Set(value.fonts.map(({ fontId }) => fontId));
    for (const [fontIndex, font] of value.fonts.entries()) {
        const ranges = [...font.unicodeRanges].sort((left, right) => left.startCodePoint - right.startCodePoint || left.endCodePoint - right.endCodePoint);
        for (const [rangeIndex, range] of ranges.entries()) {
            if (range.startCodePoint <= 0xdfff && range.endCodePoint >= 0xd800)
                context.addIssue({
                    code: "custom",
                    path: ["fonts", fontIndex, "unicodeRanges"],
                    message: "Unicode font coverage ranges cannot include the surrogate interval.",
                });
            if (rangeIndex > 0 && range.startCodePoint <= ranges[rangeIndex - 1].endCodePoint)
                context.addIssue({
                    code: "custom",
                    path: ["fonts", fontIndex, "unicodeRanges"],
                    message: "Unicode font coverage ranges must be unique and non-overlapping.",
                });
        }
    }
    for (const [chainIndex, chain] of value.fallbackChains.entries())
        for (const fontId of chain.fontIds)
            if (!fontIds.has(fontId))
                context.addIssue({
                    code: "custom",
                    path: ["fallbackChains", chainIndex, "fontIds"],
                    message: `Fallback chain references unknown font ${fontId}.`,
                });
});
export const bookFontRegistryV1Schema = fontRegistryBodySchema.safeExtend({
    fontRegistryDigestSha256: sha256Schema,
});
export const bookPageFunctionalPurposeSchema = z.enum([
    "READING",
    "NAVIGATION",
    "ATTRIBUTION",
    "CHRONOLOGY",
    "EVIDENCE",
    "SAFETY",
    "PACING",
    "VOICE",
    "ACCESSIBILITY",
    "PRODUCTION",
]);
export const bookPageModeSchema = z.enum(["PAGE", "FACING_SPREAD"]);
export const bookPageSideSchema = z.enum(["VERSO", "RECTO"]);
export const bookPageEvidenceKindSchema = z.enum([
    "CUSTOMER_APPROVED",
    "PRINCIPAL_APPROVED",
    "GUARDIAN_APPROVED",
    "EXACT_SOURCE",
    "PRODUCT_POLICY",
]);
export const bookPageRequiredEvidenceV1Schema = z
    .object({
    factCode: safeCodeSchema,
    allowedKinds: z.array(bookPageEvidenceKindSchema).min(1).max(5),
    requiredAuthoritySubjectIds: z.array(opaqueIdSchema).max(20),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.allowedKinds, context, ["allowedKinds"], "Allowed evidence kinds");
    unique(value.requiredAuthoritySubjectIds, context, ["requiredAuthoritySubjectIds"], "Required authorities");
});
export const bookPageSemanticEvidenceBindingV1Schema = z
    .object({
    evidenceId: opaqueIdSchema,
    factCode: safeCodeSchema,
    kind: bookPageEvidenceKindSchema,
    decisionId: opaqueIdSchema,
    decisionRevision: z.number().int().positive(),
    decisionValueDigestSha256: sha256Schema,
    authoritySubjectIds: z.array(opaqueIdSchema).max(20),
    sourceBindingIds: z.array(opaqueIdSchema).max(100),
    evidenceDigestSha256: sha256Schema,
})
    .strict()
    .superRefine((value, context) => {
    unique(value.authoritySubjectIds, context, ["authoritySubjectIds"], "Evidence authorities");
    unique(value.sourceBindingIds, context, ["sourceBindingIds"], "Evidence sources");
});
export const bookPageContextSignalBindingV1Schema = z
    .object({ signalId: opaqueIdSchema, valueDigestSha256: sha256Schema })
    .strict();
export const bookProtectedTextResourceBindingV1Schema = z
    .object({
    resourceId: opaqueIdSchema,
    objectVersionId: z.string().trim().min(1).max(500),
    protectedObjectChecksumSha256: sha256Schema,
    resourceOrigin: z.enum(["EXACT_SOURCE", "AUTHORIZED_DERIVATIVE", "AUTHORIZED_DECISION_VALUE"]),
    derivationBinding: bookProductionVersionBindingV1Schema.nullable(),
    derivationDigestSha256: sha256Schema.nullable(),
    rendererInputMacSha256: sha256Schema,
    rendererInputMacPolicyBinding: bookProductionVersionBindingV1Schema,
    authorizedEvidenceIds: z.array(opaqueIdSchema).max(100),
    authorizedSourceRecords: z
        .array(z
        .object({
        sourceBindingId: opaqueIdSchema,
        revision: z.number().int().positive(),
        checksumSha256: sha256Schema,
        objectVersionId: z.string().trim().min(1).max(500),
    })
        .strict())
        .max(100),
    authorizedSubjectIds: z.array(opaqueIdSchema).max(20),
    authorizationBindingDigestSha256: sha256Schema,
})
    .strict()
    .superRefine((value, context) => {
    unique(value.authorizedEvidenceIds, context, ["authorizedEvidenceIds"], "Authorized evidence");
    unique(value.authorizedSourceRecords.map(({ sourceBindingId }) => sourceBindingId), context, ["authorizedSourceRecords"], "Authorized sources");
    unique(value.authorizedSubjectIds, context, ["authorizedSubjectIds"], "Authorized subjects");
    if (value.resourceOrigin === "EXACT_SOURCE" &&
        (value.authorizedSourceRecords.length !== 1 ||
            value.derivationBinding !== null ||
            value.derivationDigestSha256 !== null ||
            value.objectVersionId !== value.authorizedSourceRecords[0]?.objectVersionId ||
            value.protectedObjectChecksumSha256 !== value.authorizedSourceRecords[0]?.checksumSha256))
        context.addIssue({
            code: "custom",
            path: ["resourceOrigin"],
            message: "Exact-source text binds one exact source object and no derivative declaration.",
        });
    if (value.resourceOrigin === "AUTHORIZED_DERIVATIVE" &&
        (value.authorizedSourceRecords.length === 0 ||
            value.derivationBinding === null ||
            value.derivationDigestSha256 === null))
        context.addIssue({
            code: "custom",
            path: ["resourceOrigin"],
            message: "Derived text requires exact source records plus transform/policy lineage bindings.",
        });
    if (value.resourceOrigin === "AUTHORIZED_DECISION_VALUE" &&
        (value.authorizedSourceRecords.length !== 0 ||
            value.authorizedEvidenceIds.length === 0 ||
            value.derivationBinding === null ||
            value.derivationDigestSha256 === null))
        context.addIssue({
            code: "custom",
            path: ["resourceOrigin"],
            message: "Decision-value text requires exact evidence plus compiler/policy lineage and no invented source object.",
        });
});
export const bookPageOverlapPermissionV1Schema = z
    .object({
    counterpartSlotId: opaqueIdSchema,
    purpose: z.enum([
        "BACKGROUND_MEDIA_WITH_LIVE_CONTENT",
        "CAPTION_ON_MEDIA",
        "FUNCTIONAL_OVERLAY",
    ]),
    policyBinding: bookProductionVersionBindingV1Schema,
})
    .strict();
export const bookPageTokenBindingV1Schema = z
    .object({
    tokenId: opaqueIdSchema,
    tokenClass: bookVisualTokenClassSchema,
    valueDigestSha256: sha256Schema,
})
    .strict();
const tokenAppliedValueSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("INTEGER"), value: coordinateSchema }).strict(),
    z.object({ kind: z.literal("OPAQUE_ID"), value: opaqueIdSchema }).strict(),
    z.object({ kind: z.literal("CODE"), value: safeCodeSchema }).strict(),
    z
        .object({
        kind: z.literal("COLOR_RGBA"),
        value: z.string().regex(/^#[A-F0-9]{8}$/u),
    })
        .strict(),
]);
export const bookPageTokenApplicationV1Schema = z
    .object({
    token: bookPageTokenBindingV1Schema,
    componentId: opaqueIdSchema,
    targetProperty: z.enum([
        "PLACEMENT_X_MILLI_POINTS",
        "PLACEMENT_Y_MILLI_POINTS",
        "PLACEMENT_WIDTH_MILLI_POINTS",
        "PLACEMENT_HEIGHT_MILLI_POINTS",
        "Z_INDEX",
        "FONT_SIZE_MILLI_POINTS",
        "LINE_HEIGHT_MILLI_POINTS",
        "FALLBACK_CHAIN_ID",
        "FILL_COLOR_RGBA",
        "STROKE_COLOR_RGBA",
        "TYPEFACE_FONT_ID",
        "LETTER_SPACING_MILLI_POINTS",
        "SPACING_MILLI_POINTS",
        "RULE_WIDTH_MILLI_POINTS",
        "MEDIA_OPACITY_MILLIONTHS",
        "NAVIGATION_MARKER_CODE",
    ]),
    appliedValue: tokenAppliedValueSchema,
    consumerBinding: bookProductionVersionBindingV1Schema,
    applicationDigestSha256: sha256Schema,
})
    .strict()
    .superRefine((value, context) => {
    const expected = {
        PLACEMENT_X_MILLI_POINTS: ["GEOMETRY", "INTEGER"],
        PLACEMENT_Y_MILLI_POINTS: ["GEOMETRY", "INTEGER"],
        PLACEMENT_WIDTH_MILLI_POINTS: ["GEOMETRY", "INTEGER"],
        PLACEMENT_HEIGHT_MILLI_POINTS: ["GEOMETRY", "INTEGER"],
        Z_INDEX: ["GEOMETRY", "INTEGER"],
        FONT_SIZE_MILLI_POINTS: ["TYPOGRAPHY", "INTEGER"],
        LINE_HEIGHT_MILLI_POINTS: ["TYPOGRAPHY", "INTEGER"],
        FALLBACK_CHAIN_ID: ["TYPOGRAPHY", "OPAQUE_ID"],
        FILL_COLOR_RGBA: ["COLOR", "COLOR_RGBA"],
        STROKE_COLOR_RGBA: ["COLOR", "COLOR_RGBA"],
        TYPEFACE_FONT_ID: ["TYPOGRAPHY", "OPAQUE_ID"],
        LETTER_SPACING_MILLI_POINTS: ["TYPOGRAPHY", "INTEGER"],
        SPACING_MILLI_POINTS: ["SPACING", "INTEGER"],
        RULE_WIDTH_MILLI_POINTS: ["RULE", "INTEGER"],
        MEDIA_OPACITY_MILLIONTHS: ["MEDIA_TREATMENT", "INTEGER"],
        NAVIGATION_MARKER_CODE: ["NAVIGATION", "CODE"],
    };
    const [tokenClass, valueKind] = expected[value.targetProperty];
    if (value.token.tokenClass !== tokenClass || value.appliedValue.kind !== valueKind)
        context.addIssue({
            code: "custom",
            path: ["targetProperty"],
            message: "Token target, token class, and explicit applied-value kind must agree.",
        });
    if (value.targetProperty === "MEDIA_OPACITY_MILLIONTHS" &&
        value.appliedValue.kind === "INTEGER" &&
        (value.appliedValue.value <= 0 || value.appliedValue.value > 1_000_000))
        context.addIssue({
            code: "custom",
            path: ["appliedValue"],
            message: "A material media-opacity application must be an exact positive integer millionths value through one million.",
        });
    if (value.targetProperty === "RULE_WIDTH_MILLI_POINTS" &&
        value.appliedValue.kind === "INTEGER" &&
        value.appliedValue.value <= 0)
        context.addIssue({
            code: "custom",
            path: ["appliedValue"],
            message: "A material rule-width application must be positive.",
        });
    if (value.targetProperty === "SPACING_MILLI_POINTS" &&
        value.appliedValue.kind === "INTEGER" &&
        value.appliedValue.value === 0)
        context.addIssue({
            code: "custom",
            path: ["appliedValue"],
            message: "A zero-spacing application cannot count as a material visual change.",
        });
    if ((value.targetProperty === "FILL_COLOR_RGBA" ||
        value.targetProperty === "STROKE_COLOR_RGBA") &&
        value.appliedValue.kind === "COLOR_RGBA" &&
        value.appliedValue.value.endsWith("00"))
        context.addIssue({
            code: "custom",
            path: ["appliedValue"],
            message: "A fully transparent color cannot count as a material visual application.",
        });
});
export const bookPageDirectionFlagBindingV1Schema = z
    .object({
    path: z.string().trim().min(1).max(300),
    valueDigestSha256: sha256Schema,
    componentId: opaqueIdSchema,
    effects: z
        .array(z
        .object({
        targetProperty: z.enum([
            "PLACEMENT_X_MILLI_POINTS",
            "PLACEMENT_Y_MILLI_POINTS",
            "PLACEMENT_WIDTH_MILLI_POINTS",
            "PLACEMENT_HEIGHT_MILLI_POINTS",
            "Z_INDEX",
            "FONT_SIZE_MILLI_POINTS",
            "LINE_HEIGHT_MILLI_POINTS",
            "FALLBACK_CHAIN_ID",
            "FILL_COLOR_RGBA",
            "STROKE_COLOR_RGBA",
            "TYPEFACE_FONT_ID",
            "LETTER_SPACING_MILLI_POINTS",
            "SPACING_MILLI_POINTS",
            "RULE_WIDTH_MILLI_POINTS",
            "MEDIA_OPACITY_MILLIONTHS",
            "NAVIGATION_MARKER_CODE",
        ]),
        visualAxis: bookVisualIntentAxisSchema,
        intentSignalIds: z.array(opaqueIdSchema).min(1).max(40),
        mappingPolicyBinding: bookProductionVersionBindingV1Schema,
        appliedValue: tokenAppliedValueSchema,
        consumerBinding: bookProductionVersionBindingV1Schema,
        effectDigestSha256: sha256Schema,
    })
        .strict())
        .min(1)
        .max(40),
    applicationDigestSha256: sha256Schema,
})
    .strict()
    .superRefine((value, context) => {
    const integerTargets = new Set([
        "PLACEMENT_X_MILLI_POINTS",
        "PLACEMENT_Y_MILLI_POINTS",
        "PLACEMENT_WIDTH_MILLI_POINTS",
        "PLACEMENT_HEIGHT_MILLI_POINTS",
        "Z_INDEX",
        "FONT_SIZE_MILLI_POINTS",
        "LINE_HEIGHT_MILLI_POINTS",
        "LETTER_SPACING_MILLI_POINTS",
        "SPACING_MILLI_POINTS",
        "RULE_WIDTH_MILLI_POINTS",
        "MEDIA_OPACITY_MILLIONTHS",
    ]);
    const colorTargets = new Set(["FILL_COLOR_RGBA", "STROKE_COLOR_RGBA"]);
    const opaqueTargets = new Set(["FALLBACK_CHAIN_ID", "TYPEFACE_FONT_ID"]);
    unique(value.effects.map(({ targetProperty }) => targetProperty), context, ["effects"], "Direction-flag effect targets");
    for (const [index, effect] of value.effects.entries()) {
        unique(effect.intentSignalIds, context, ["effects", index, "intentSignalIds"], "Direction-effect intent signals");
        const validKind = integerTargets.has(effect.targetProperty)
            ? effect.appliedValue.kind === "INTEGER"
            : colorTargets.has(effect.targetProperty)
                ? effect.appliedValue.kind === "COLOR_RGBA"
                : opaqueTargets.has(effect.targetProperty)
                    ? effect.appliedValue.kind === "OPAQUE_ID"
                    : effect.appliedValue.kind === "CODE";
        if (!validKind)
            context.addIssue({
                code: "custom",
                path: ["effects", index, "appliedValue"],
                message: "Direction flag effect target and explicit applied-value kind must agree.",
            });
        if (effect.targetProperty === "RULE_WIDTH_MILLI_POINTS" &&
            effect.appliedValue.kind === "INTEGER" &&
            effect.appliedValue.value <= 0)
            context.addIssue({
                code: "custom",
                path: ["effects", index],
                message: "A material rule effect must be positive.",
            });
        if (effect.targetProperty === "SPACING_MILLI_POINTS" &&
            effect.appliedValue.kind === "INTEGER" &&
            effect.appliedValue.value === 0)
            context.addIssue({
                code: "custom",
                path: ["effects", index],
                message: "A zero-spacing effect cannot count as a material direction change.",
            });
        if (effect.targetProperty === "MEDIA_OPACITY_MILLIONTHS" &&
            effect.appliedValue.kind === "INTEGER" &&
            (effect.appliedValue.value <= 0 || effect.appliedValue.value > 1_000_000))
            context.addIssue({
                code: "custom",
                path: ["effects", index],
                message: "A material opacity effect must be positive and at most one million.",
            });
        if ((effect.targetProperty === "FILL_COLOR_RGBA" ||
            effect.targetProperty === "STROKE_COLOR_RGBA") &&
            effect.appliedValue.kind === "COLOR_RGBA" &&
            effect.appliedValue.value.endsWith("00"))
            context.addIssue({
                code: "custom",
                path: ["effects", index],
                message: "A fully transparent color cannot count as a material direction effect.",
            });
    }
});
export const bookTextFlowPolicyV1Schema = z
    .object({
    horizontalAlignment: z.enum(["START", "CENTER", "END", "JUSTIFY"]),
    verticalAlignment: z.enum(["TOP", "CENTER", "BOTTOM"]),
    paragraphSpacingMilliPoints: z.number().int().nonnegative().max(2_000_000),
    firstLineIndentMilliPoints: coordinateSchema,
    columnCount: z.number().int().positive().max(12),
    hyphenation: z.enum(["DISABLED", "LANGUAGE_DICTIONARY_BOUND"]),
    lineBreakPolicy: z.enum(["UNICODE_UAX14_BOUND", "EXPLICIT_BREAKS_ONLY"]),
    minimumWidowLines: z.number().int().positive().max(20),
    minimumOrphanLines: z.number().int().positive().max(20),
})
    .strict();
const normalizedPathPointSchema = z
    .object({
    xMillionths: z.number().int().nonnegative().max(1_000_000),
    yMillionths: z.number().int().nonnegative().max(1_000_000),
})
    .strict();
export const bookNormalizedVectorPathCommandV1Schema = z.discriminatedUnion("opcode", [
    z.object({ opcode: z.literal("MOVE_TO"), ...normalizedPathPointSchema.shape }).strict(),
    z.object({ opcode: z.literal("LINE_TO"), ...normalizedPathPointSchema.shape }).strict(),
    z
        .object({
        opcode: z.literal("QUADRATIC_TO"),
        controlXMillionths: z.number().int().nonnegative().max(1_000_000),
        controlYMillionths: z.number().int().nonnegative().max(1_000_000),
        ...normalizedPathPointSchema.shape,
    })
        .strict(),
    z
        .object({
        opcode: z.literal("CUBIC_TO"),
        control1XMillionths: z.number().int().nonnegative().max(1_000_000),
        control1YMillionths: z.number().int().nonnegative().max(1_000_000),
        control2XMillionths: z.number().int().nonnegative().max(1_000_000),
        control2YMillionths: z.number().int().nonnegative().max(1_000_000),
        ...normalizedPathPointSchema.shape,
    })
        .strict(),
    z.object({ opcode: z.literal("CLOSE") }).strict(),
]);
export const bookResolvedVectorPathCommandV1Schema = z.discriminatedUnion("opcode", [
    z
        .object({
        opcode: z.literal("MOVE_TO"),
        xMilliPoints: coordinateSchema,
        yMilliPoints: coordinateSchema,
    })
        .strict(),
    z
        .object({
        opcode: z.literal("LINE_TO"),
        xMilliPoints: coordinateSchema,
        yMilliPoints: coordinateSchema,
    })
        .strict(),
    z
        .object({
        opcode: z.literal("QUADRATIC_TO"),
        controlXMilliPoints: coordinateSchema,
        controlYMilliPoints: coordinateSchema,
        xMilliPoints: coordinateSchema,
        yMilliPoints: coordinateSchema,
    })
        .strict(),
    z
        .object({
        opcode: z.literal("CUBIC_TO"),
        control1XMilliPoints: coordinateSchema,
        control1YMilliPoints: coordinateSchema,
        control2XMilliPoints: coordinateSchema,
        control2YMilliPoints: coordinateSchema,
        xMilliPoints: coordinateSchema,
        yMilliPoints: coordinateSchema,
    })
        .strict(),
    z.object({ opcode: z.literal("CLOSE") }).strict(),
]);
export const bookResolvedVisualPropertyV1Schema = z
    .object({
    componentId: opaqueIdSchema,
    targetProperty: z.enum([
        "PLACEMENT_X_MILLI_POINTS",
        "PLACEMENT_Y_MILLI_POINTS",
        "PLACEMENT_WIDTH_MILLI_POINTS",
        "PLACEMENT_HEIGHT_MILLI_POINTS",
        "Z_INDEX",
        "FONT_SIZE_MILLI_POINTS",
        "LINE_HEIGHT_MILLI_POINTS",
        "FALLBACK_CHAIN_ID",
        "FILL_COLOR_RGBA",
        "STROKE_COLOR_RGBA",
        "TYPEFACE_FONT_ID",
        "LETTER_SPACING_MILLI_POINTS",
        "SPACING_MILLI_POINTS",
        "RULE_WIDTH_MILLI_POINTS",
        "MEDIA_OPACITY_MILLIONTHS",
        "NAVIGATION_MARKER_CODE",
    ]),
    appliedValue: tokenAppliedValueSchema,
    source: z.enum(["TOKEN", "DIRECTION_FLAG_EFFECT"]),
    sourceId: z.string().trim().min(1).max(500),
    sourceApplicationDigestSha256: sha256Schema,
    primitiveField: z.enum([
        "PLACEMENT_X",
        "PLACEMENT_Y",
        "PLACEMENT_WIDTH",
        "PLACEMENT_HEIGHT",
        "PLACEMENT_Z_INDEX",
        "TEXT_FONT_SIZE",
        "TEXT_LINE_HEIGHT",
        "TEXT_FALLBACK_CHAIN",
        "TEXT_FILL_COLOR",
        "TEXT_PREFERRED_FONT",
        "TEXT_LETTER_SPACING",
        "TEXT_PARAGRAPH_SPACING",
        "MEDIA_OPACITY",
        "COMPONENT_VECTOR_FILL",
        "COMPONENT_VECTOR_STROKE",
        "COMPONENT_VECTOR_STROKE_WIDTH",
        "COMPONENT_RULE_WIDTH",
        "COMPONENT_RULE_COLOR",
        "COMPONENT_TEXT_FILL",
        "COMPONENT_TEXT_FONT_SIZE",
        "COMPONENT_TEXT_LINE_HEIGHT",
        "COMPONENT_TEXT_FALLBACK_CHAIN",
        "COMPONENT_TEXT_PREFERRED_FONT",
        "COMPONENT_TEXT_LETTER_SPACING",
        "COMPONENT_TEXT_PARAGRAPH_SPACING",
        "COMPONENT_MEDIA_OPACITY",
        "COMPONENT_NAVIGATION_MARKER",
        "SURFACE_BACKGROUND_FILL",
    ]),
    primitiveCommandId: opaqueIdSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    const componentCommandFields = new Set([
        "COMPONENT_VECTOR_FILL",
        "COMPONENT_VECTOR_STROKE",
        "COMPONENT_VECTOR_STROKE_WIDTH",
        "COMPONENT_RULE_WIDTH",
        "COMPONENT_RULE_COLOR",
        "COMPONENT_TEXT_FILL",
        "COMPONENT_TEXT_FONT_SIZE",
        "COMPONENT_TEXT_LINE_HEIGHT",
        "COMPONENT_TEXT_FALLBACK_CHAIN",
        "COMPONENT_TEXT_PREFERRED_FONT",
        "COMPONENT_TEXT_LETTER_SPACING",
        "COMPONENT_TEXT_PARAGRAPH_SPACING",
        "COMPONENT_MEDIA_OPACITY",
        "COMPONENT_NAVIGATION_MARKER",
    ]);
    if (componentCommandFields.has(value.primitiveField) !== (value.primitiveCommandId !== null))
        context.addIssue({
            code: "custom",
            path: ["primitiveCommandId"],
            message: "Only exact component-command primitive fields may carry a command identity.",
        });
});
export const bookComponentPrimitiveCommandV1Schema = z.discriminatedUnion("commandKind", [
    z
        .object({
        commandKind: z.literal("LIVE_TEXT_PAINT"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        targetCommandId: opaqueIdSchema,
        fillColorRgba: z.string().regex(/^#[A-F0-9]{6}(?:0[1-9A-F]|[1-9A-F][0-9A-F])$/u),
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("LIVE_TEXT_TYPOGRAPHY"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        targetCommandId: opaqueIdSchema,
        preferredFontId: opaqueIdSchema,
        fallbackChainId: opaqueIdSchema,
        fontSizeMilliPoints: z.number().int().positive().max(1_000_000),
        lineHeightMilliPoints: z.number().int().positive().max(2_000_000),
        letterSpacingMilliPoints: coordinateSchema,
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("LIVE_TEXT_FLOW"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        targetCommandId: opaqueIdSchema,
        textFlowPolicy: bookTextFlowPolicyV1Schema,
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("MEDIA_TREATMENT"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        targetCommandId: opaqueIdSchema,
        fitMode: z.enum(["EXACT_CROP_FILL", "CONTAIN", "EXACT_PIXEL_SIZE"]),
        opacityMillionths: z.number().int().positive().max(1_000_000),
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("VECTOR_PATH"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        pathDigestSha256: sha256Schema,
        pathCommands: z.array(bookNormalizedVectorPathCommandV1Schema).min(2).max(2_000),
        boundsNormalized: z
            .object({
            xMillionths: z.number().int().nonnegative().max(999_999),
            yMillionths: z.number().int().nonnegative().max(999_999),
            widthMillionths: z.number().int().positive().max(1_000_000),
            heightMillionths: z.number().int().positive().max(1_000_000),
        })
            .strict()
            .refine((bounds) => bounds.xMillionths + bounds.widthMillionths <= 1_000_000 &&
            bounds.yMillionths + bounds.heightMillionths <= 1_000_000, "Normalized vector bounds must stay inside the component reference box."),
        fillColorRgba: z
            .string()
            .regex(/^#[A-F0-9]{8}$/u)
            .nullable(),
        strokeColorRgba: z
            .string()
            .regex(/^#[A-F0-9]{8}$/u)
            .nullable(),
        strokeWidthMillionths: z.number().int().positive().max(1_000_000).nullable(),
    })
        .strict()
        .superRefine(({ fillColorRgba, strokeColorRgba, strokeWidthMillionths, pathCommands }, context) => {
        if (pathCommands[0]?.opcode !== "MOVE_TO")
            context.addIssue({
                code: "custom",
                path: ["pathCommands", 0],
                message: "A canonical vector path must begin with MOVE_TO.",
            });
        if (fillColorRgba !== null && pathCommands.at(-1)?.opcode !== "CLOSE")
            context.addIssue({
                code: "custom",
                path: ["pathCommands"],
                message: "A filled canonical vector path must end with CLOSE.",
            });
        if ((fillColorRgba === null || fillColorRgba.endsWith("00")) &&
            (strokeColorRgba === null ||
                strokeColorRgba.endsWith("00") ||
                strokeWidthMillionths === null))
            context.addIssue({
                code: "custom",
                message: "Vector paths require a nontransparent fill or a nontransparent positive-width stroke.",
            });
        if ((strokeColorRgba === null) !== (strokeWidthMillionths === null))
            context.addIssue({
                code: "custom",
                path: ["strokeColorRgba"],
                message: "Stroke color and width must coexist exactly.",
            });
    }),
    z
        .object({
        commandKind: z.literal("RULE"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        fromXMillionths: z.number().int().nonnegative().max(1_000_000),
        fromYMillionths: z.number().int().nonnegative().max(1_000_000),
        toXMillionths: z.number().int().nonnegative().max(1_000_000),
        toYMillionths: z.number().int().nonnegative().max(1_000_000),
        widthMillionths: z.number().int().positive().max(1_000_000),
        colorRgba: z.string().regex(/^#[A-F0-9]{8}$/u),
    })
        .strict()
        .superRefine((value, context) => {
        if (value.colorRgba.endsWith("00") ||
            (value.fromXMillionths === value.toXMillionths &&
                value.fromYMillionths === value.toYMillionths))
            context.addIssue({
                code: "custom",
                message: "A rule command must be nontransparent and have distinct endpoints.",
            });
    }),
    z
        .object({
        commandKind: z.literal("LIVE_TEXT_REFERENCE"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        fieldKind: safeCodeSchema,
        markerCode: safeCodeSchema,
        navigationTargetRole: safeCodeSchema.nullable(),
        referenceOwnership: z.literal("REFERENCED_PLACEMENT_IS_SOLE_PAINT_OWNER"),
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("GOVERNED_MEDIA_REFERENCE"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        assetRoleCode: safeCodeSchema,
        referenceOwnership: z.literal("REFERENCED_PLACEMENT_IS_SOLE_PAINT_OWNER"),
        destinationBoundsNormalized: z
            .object({
            xMillionths: z.number().int().nonnegative().max(999_999),
            yMillionths: z.number().int().nonnegative().max(999_999),
            widthMillionths: z.number().int().positive().max(1_000_000),
            heightMillionths: z.number().int().positive().max(1_000_000),
        })
            .strict()
            .refine((bounds) => bounds.xMillionths + bounds.widthMillionths <= 1_000_000 &&
            bounds.yMillionths + bounds.heightMillionths <= 1_000_000, "Normalized media destination must stay inside the component reference box."),
    })
        .strict(),
]);
export const bookResolvedComponentPrimitiveCommandV1Schema = z.discriminatedUnion("commandKind", [
    z
        .object({
        commandKind: z.literal("LIVE_TEXT_PAINT"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        targetCommandId: opaqueIdSchema,
        fillColorRgba: z.string().regex(/^#[A-F0-9]{6}(?:0[1-9A-F]|[1-9A-F][0-9A-F])$/u),
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("LIVE_TEXT_TYPOGRAPHY"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        targetCommandId: opaqueIdSchema,
        preferredFontId: opaqueIdSchema,
        fallbackChainId: opaqueIdSchema,
        fontSizeMilliPoints: z.number().int().positive().max(1_000_000),
        lineHeightMilliPoints: z.number().int().positive().max(2_000_000),
        letterSpacingMilliPoints: coordinateSchema,
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("LIVE_TEXT_FLOW"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        targetCommandId: opaqueIdSchema,
        textFlowPolicy: bookTextFlowPolicyV1Schema,
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("MEDIA_TREATMENT"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        targetCommandId: opaqueIdSchema,
        fitMode: z.enum(["EXACT_CROP_FILL", "CONTAIN", "EXACT_PIXEL_SIZE"]),
        opacityMillionths: z.number().int().positive().max(1_000_000),
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("VECTOR_PATH"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        pathDigestSha256: sha256Schema,
        resolvedPathCommands: z.array(bookResolvedVectorPathCommandV1Schema).min(2).max(2_000),
        bounds: bookPageRectV1Schema,
        fillColorRgba: z
            .string()
            .regex(/^#[A-F0-9]{8}$/u)
            .nullable(),
        strokeColorRgba: z
            .string()
            .regex(/^#[A-F0-9]{8}$/u)
            .nullable(),
        strokeWidthMilliPoints: z.number().int().positive().max(2_000_000).nullable(),
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("RULE"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        fromXMilliPoints: coordinateSchema,
        fromYMilliPoints: coordinateSchema,
        toXMilliPoints: coordinateSchema,
        toYMilliPoints: coordinateSchema,
        widthMilliPoints: z.number().int().positive().max(2_000_000),
        colorRgba: z.string().regex(/^#[A-F0-9]{6}(?:0[1-9A-F]|[1-9A-F][0-9A-F])$/u),
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("LIVE_TEXT_REFERENCE"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        fieldKind: safeCodeSchema,
        markerCode: safeCodeSchema,
        navigationTargetRole: safeCodeSchema.nullable(),
        navigationTarget: z
            .object({
            targetKind: z.enum(["SURFACE", "PAGE"]),
            targetId: opaqueIdSchema,
            orderIndex: z.number().int().nonnegative().max(10_000),
        })
            .strict()
            .nullable(),
        referenceOwnership: z.literal("REFERENCED_PLACEMENT_IS_SOLE_PAINT_OWNER"),
        visibleFieldNodeId: opaqueIdSchema,
    })
        .strict(),
    z
        .object({
        commandKind: z.literal("GOVERNED_MEDIA_REFERENCE"),
        commandId: opaqueIdSchema,
        commandOrderIndex: z.number().int().nonnegative().max(10_000),
        zIndex: z.number().int().min(-1_000).max(1_000),
        assetRoleCode: safeCodeSchema,
        referenceOwnership: z.literal("REFERENCED_PLACEMENT_IS_SOLE_PAINT_OWNER"),
        assetUseBinding: bookPublishingAssetUseExactBindingV1Schema,
        destinationBounds: bookPageRectV1Schema,
    })
        .strict(),
]);
export const bookSurfaceBackgroundPrimitiveV1Schema = z.discriminatedUnion("paintKind", [
    z
        .object({
        paintKind: z.literal("NO_ADDITIONAL_PAINT"),
    })
        .strict(),
    z
        .object({
        paintKind: z.literal("SOLID_RGBA"),
        colorRgba: z.string().regex(/^#[A-F0-9]{6}(?:0[1-9A-F]|[1-9A-F][0-9A-F])$/u),
        componentId: opaqueIdSchema,
        sourceApplicationDigestSha256: sha256Schema,
    })
        .strict(),
]);
export const bookSurfaceBackgroundPolicyV1Schema = z
    .object({
    primitive: bookSurfaceBackgroundPrimitiveV1Schema,
    sourceTokenApplication: bookPageTokenApplicationV1Schema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.primitive.paintKind === "NO_ADDITIONAL_PAINT") {
        if (value.sourceTokenApplication !== null)
            context.addIssue({
                code: "custom",
                path: ["sourceTokenApplication"],
                message: "An explicit no-paint background cannot claim a paint-token consumer.",
            });
        return;
    }
    const application = value.sourceTokenApplication;
    if (!application ||
        application.targetProperty !== "FILL_COLOR_RGBA" ||
        application.componentId !== value.primitive.componentId ||
        application.applicationDigestSha256 !== value.primitive.sourceApplicationDigestSha256 ||
        application.appliedValue.kind !== "COLOR_RGBA" ||
        application.appliedValue.value !== value.primitive.colorRgba)
        context.addIssue({
            code: "custom",
            path: ["sourceTokenApplication"],
            message: "A solid surface background must be controlled by one exact fill-token application.",
        });
});
export const bookResolvedRenderPrimitiveV1Schema = z.discriminatedUnion("primitiveKind", [
    z
        .object({
        primitiveKind: z.literal("LIVE_TEXT"),
        nodeId: opaqueIdSchema,
        expectedBounds: bookPageRectV1Schema,
        preferredFontId: opaqueIdSchema,
        fallbackChainId: opaqueIdSchema,
        fontSizeMilliPoints: z.number().int().positive().max(1_000_000),
        lineHeightMilliPoints: z.number().int().positive().max(2_000_000),
        letterSpacingMilliPoints: coordinateSchema,
        finalFillColorRgba: z.string().regex(/^#[A-F0-9]{6}(?:0[1-9A-F]|[1-9A-F][0-9A-F])$/u),
        textFlowPolicy: bookTextFlowPolicyV1Schema,
    })
        .strict(),
    z
        .object({
        primitiveKind: z.literal("MEDIA"),
        nodeId: opaqueIdSchema,
        assetUseBinding: bookPublishingAssetUseExactBindingV1Schema,
        fitMode: z.enum(["EXACT_CROP_FILL", "CONTAIN", "EXACT_PIXEL_SIZE"]),
        sourceCropPixels: z
            .object({
            x: z.number().int().nonnegative().max(1_000_000),
            y: z.number().int().nonnegative().max(1_000_000),
            width: z.number().int().positive().max(1_000_000),
            height: z.number().int().positive().max(1_000_000),
        })
            .strict(),
        destinationBounds: bookPageRectV1Schema,
        finalOpacityMillionths: z.number().int().positive().max(1_000_000),
    })
        .strict(),
    z
        .object({
        primitiveKind: z.literal("COMPONENT"),
        nodeId: opaqueIdSchema,
        componentId: opaqueIdSchema,
        primitiveProgramBinding: bookProductionVersionBindingV1Schema,
        primitiveProgramDigestSha256: sha256Schema,
        primitiveCoordinateSpace: z.literal("NORMALIZED_MILLIONTHS"),
        resolvedCoordinateSpace: z.literal("PLACEMENT_LOCAL_MILLI_POINTS"),
        coordinateTransformPolicy: z.literal("NORMALIZED_MILLIONTHS_FLOOR_EDGES_V1"),
        instantiationDigestSha256: sha256Schema,
        resolvedCommandStreamDigestSha256: sha256Schema,
        resolvedCommands: z.array(bookResolvedComponentPrimitiveCommandV1Schema).min(1).max(500),
        destinationBounds: bookPageRectV1Schema,
        visibleFieldBindings: z
            .array(z
            .object({
            commandId: opaqueIdSchema,
            fieldKind: safeCodeSchema,
            nodeId: opaqueIdSchema,
        })
            .strict())
            .max(40),
        governedMediaBindings: z
            .array(z
            .object({
            commandId: opaqueIdSchema,
            assetRoleCode: safeCodeSchema,
            assetUseBinding: bookPublishingAssetUseExactBindingV1Schema,
            destinationBounds: bookPageRectV1Schema,
        })
            .strict())
            .max(40),
    })
        .strict()
        .superRefine((value, context) => {
        unique(value.resolvedCommands.map(({ commandId }) => commandId), context, ["resolvedCommands"], "Resolved component commands");
        unique(value.visibleFieldBindings.map(({ commandId }) => commandId), context, ["visibleFieldBindings"], "Resolved component visible fields");
        unique(value.governedMediaBindings.map(({ commandId }) => commandId), context, ["governedMediaBindings"], "Resolved component media roles");
        const ordered = [...value.resolvedCommands].sort((left, right) => left.commandOrderIndex - right.commandOrderIndex);
        if (ordered.some((command, index) => command.commandOrderIndex !== index))
            context.addIssue({
                code: "custom",
                path: ["resolvedCommands"],
                message: "Component primitive command order must be unique and contiguous from zero.",
            });
        if (ordered.some((command, index) => index > 0 && command.zIndex < ordered[index - 1].zIndex))
            context.addIssue({
                code: "custom",
                path: ["resolvedCommands"],
                message: "Component command order is the exact render order and must be nondecreasing by local z-index.",
            });
    }),
]);
export const bookTextControlPolicyV1Schema = z
    .object({
    ascii: z.enum(["NONE", "LINE_FEED", "LINE_FEED_AND_TAB"]),
    allowedBidiControlCodePoints: z.array(z.number().int().nonnegative().max(0x10ffff)).max(20),
    allowedFormatControlCodePoints: z.array(z.number().int().nonnegative().max(0x10ffff)).max(40),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.allowedBidiControlCodePoints.map(String), context, ["allowedBidiControlCodePoints"], "Allowed bidi controls");
    const bidiControls = new Set([
        0x061c, 0x200e, 0x200f, 0x202a, 0x202b, 0x202c, 0x202d, 0x202e, 0x2066, 0x2067, 0x2068,
        0x2069,
    ]);
    if (value.allowedBidiControlCodePoints.some((point) => !bidiControls.has(point)))
        context.addIssue({
            code: "custom",
            path: ["allowedBidiControlCodePoints"],
            message: "Only Unicode bidi-control scalars may be allowlisted.",
        });
    unique(value.allowedFormatControlCodePoints.map(String), context, ["allowedFormatControlCodePoints"], "Allowed format controls");
    const formatControls = new Set([
        0x00ad, 0x034f, 0x180e, 0x200b, 0x200c, 0x200d, 0x2060, 0x2061, 0x2062, 0x2063, 0x2064,
        0x206a, 0x206b, 0x206c, 0x206d, 0x206e, 0x206f, 0xfeff,
    ]);
    if (value.allowedFormatControlCodePoints.some((point) => !formatControls.has(point)))
        context.addIssue({
            code: "custom",
            path: ["allowedFormatControlCodePoints"],
            message: "Only the bounded supported Unicode format-control set may be allowlisted.",
        });
});
const cardinalitySchema = z
    .object({
    minimum: z.number().int().nonnegative().max(1),
    maximum: z.literal(1),
})
    .strict()
    .refine(({ minimum, maximum }) => minimum <= maximum, "Slot cardinality must be ordered.");
const slotBase = {
    slotId: opaqueIdSchema,
    implementationComponentIds: z.array(opaqueIdSchema).min(1).max(100),
    acceptedContentKinds: z.array(safeCodeSchema).min(1).max(100),
    rect: bookPageRectV1Schema,
    physicalPageSide: bookPageSideSchema.nullable(),
    attributionPositionCode: safeCodeSchema.nullable(),
    cardinality: cardinalitySchema,
    purposes: z.array(bookPageFunctionalPurposeSchema).min(1).max(10),
    zIndex: z.number().int().min(-1_000).max(1_000),
    accessibility: z.enum(["READING_ORDER", "ALTERNATE_DESCRIPTION", "ARTIFACT"]),
    overlapPermissions: z.array(bookPageOverlapPermissionV1Schema).max(100),
    tokenApplications: z.array(bookPageTokenApplicationV1Schema).min(1).max(100),
    directionFlagBindings: z.array(bookPageDirectionFlagBindingV1Schema).max(100),
};
const textSlotSchema = z
    .object({
    ...slotBase,
    nodeKind: z.literal("TEXT"),
    placementZone: z.literal("SAFE_CONTENT"),
    maximumControlPolicy: bookTextControlPolicyV1Schema,
    resolvedTextStyle: z
        .object({
        preferredFontId: opaqueIdSchema,
        fallbackChainId: opaqueIdSchema,
        fontSizeMilliPoints: z.number().int().positive().max(1_000_000),
        lineHeightMilliPoints: z.number().int().positive().max(2_000_000),
        letterSpacingMilliPoints: coordinateSchema,
        fillColorRgba: z.string().regex(/^#[A-F0-9]{6}(?:0[1-9A-F]|[1-9A-F][0-9A-F])$/u),
    })
        .strict(),
    textFlowPolicy: bookTextFlowPolicyV1Schema,
    capacity: z
        .object({
        minimumCodePoints: z.number().int().nonnegative().max(1_000_000),
        maximumCodePoints: z.number().int().positive().max(1_000_000),
        minimumGraphemes: z.number().int().nonnegative().max(1_000_000),
        maximumGraphemes: z.number().int().positive().max(1_000_000),
        minimumLines: z.number().int().nonnegative().max(100_000),
        maximumLines: z.number().int().positive().max(100_000),
        minimumFontSizeMilliPoints: z.number().int().positive().max(1_000_000),
        maximumFontSizeMilliPoints: z.number().int().positive().max(1_000_000),
    })
        .strict(),
})
    .strict()
    .superRefine((value, context) => {
    for (const [minimum, maximum, label] of [
        [value.capacity.minimumCodePoints, value.capacity.maximumCodePoints, "code-point"],
        [value.capacity.minimumGraphemes, value.capacity.maximumGraphemes, "grapheme"],
        [value.capacity.minimumLines, value.capacity.maximumLines, "line"],
        [
            value.capacity.minimumFontSizeMilliPoints,
            value.capacity.maximumFontSizeMilliPoints,
            "font-size",
        ],
    ])
        if (minimum > maximum)
            context.addIssue({
                code: "custom",
                path: ["capacity"],
                message: `Text ${label} capacity must be ordered.`,
            });
});
const mediaSlotSchema = z
    .object({
    ...slotBase,
    nodeKind: z.literal("MEDIA"),
    placementZone: z.enum(["SAFE_CONTENT", "TRIM", "BLEED"]),
    coverageRequirement: z.enum(["WITHIN_ZONE", "COVER_ZONE_EXACT"]),
    resolvedFitMode: z.enum(["EXACT_CROP_FILL", "CONTAIN", "EXACT_PIXEL_SIZE"]),
    resolvedOpacityMillionths: z.number().int().positive().max(1_000_000),
    allowedFitModes: z
        .array(z.enum(["EXACT_CROP_FILL", "CONTAIN", "EXACT_PIXEL_SIZE"]))
        .min(1)
        .max(3),
    minimumPlacedDpi: z.number().finite().positive().max(2_400),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.allowedFitModes, context, ["allowedFitModes"], "Allowed media fit modes");
    if (!value.allowedFitModes.includes(value.resolvedFitMode))
        context.addIssue({
            code: "custom",
            path: ["resolvedFitMode"],
            message: "Resolved media fit must be one exact allowed product-grammar treatment.",
        });
    if (value.coverageRequirement === "COVER_ZONE_EXACT" && value.placementZone !== "BLEED")
        context.addIssue({
            code: "custom",
            path: ["coverageRequirement"],
            message: "Exact surface coverage is reserved for an exact bleed-zone media slot.",
        });
});
const componentSlotSchema = z
    .object({
    ...slotBase,
    nodeKind: z.literal("COMPONENT"),
    placementZone: z.enum(["SAFE_CONTENT", "TRIM"]),
    allowedComponentIds: z.array(opaqueIdSchema).min(1).max(100),
})
    .strict()
    .superRefine((value, context) => unique(value.allowedComponentIds, context, ["allowedComponentIds"], "Allowed components"));
export const bookPageGrammarSlotV1Schema = z.discriminatedUnion("nodeKind", [
    textSlotSchema,
    mediaSlotSchema,
    componentSlotSchema,
]);
export const bookPageGrammarV1Schema = z
    .object({
    grammarId: opaqueIdSchema,
    productPageRole: safeCodeSchema,
    pageMode: bookPageModeSchema,
    purposes: z.array(bookPageFunctionalPurposeSchema).min(1).max(10),
    requiredEvidence: z.array(bookPageRequiredEvidenceV1Schema).max(100),
    requiredContextSignalIds: z.array(opaqueIdSchema).max(100),
    tokenBindings: z.array(bookPageTokenBindingV1Schema).min(1).max(300),
    requiredDirectionFlagBindings: z.array(bookPageDirectionFlagBindingV1Schema).max(300),
    visualPlanGrammarDigestSha256: sha256Schema,
    visualPlanCapacity: z
        .object({
        textCharacters: z
            .object({ min: z.number().int().nonnegative(), max: z.number().int().nonnegative() })
            .strict(),
        itemCount: z
            .object({ min: z.number().int().nonnegative(), max: z.number().int().nonnegative() })
            .strict(),
        mediaCount: z
            .object({ min: z.number().int().nonnegative(), max: z.number().int().nonnegative() })
            .strict(),
    })
        .strict(),
    surfaceBackgroundPolicy: bookSurfaceBackgroundPolicyV1Schema,
    slots: z.array(bookPageGrammarSlotV1Schema).min(1).max(200),
    visualOrderSlotIds: z.array(opaqueIdSchema).min(1).max(200),
    accessibleOrderSlotIds: z.array(opaqueIdSchema).max(200),
    orderRelationshipPolicy: z.enum(["INDEPENDENT_MAY_MATCH", "MUST_DIFFER", "SAME_REQUIRED"]),
    crossSpineSlotIds: z.array(opaqueIdSchema).max(100),
    underfillAction: z.enum([
        "ALLOW_AUTHORED_WHITESPACE",
        "SELECT_ALTERNATE_GRAMMAR",
        "BLOCK_RENDER",
    ]),
    overflowAction: z.enum(["SELECT_ALTERNATE_GRAMMAR", "BLOCK_RENDER"]),
    alternateGrammarIds: z.array(opaqueIdSchema).max(40),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.purposes, context, ["purposes"], "Grammar purposes");
    unique(value.requiredEvidence.map(({ factCode }) => factCode), context, ["requiredEvidence"], "Grammar evidence facts");
    unique(value.requiredContextSignalIds, context, ["requiredContextSignalIds"], "Grammar signals");
    unique(value.tokenBindings.map(({ tokenId }) => tokenId), context, ["tokenBindings"], "Grammar token IDs");
    unique(value.requiredDirectionFlagBindings.map((binding) => exactDirectionFlagKey(binding)), context, ["requiredDirectionFlagBindings"], "Grammar direction-flag consumers");
    unique(value.slots.map(({ slotId }) => slotId), context, ["slots"], "Grammar slots");
    exactSet(value.visualOrderSlotIds, value.slots.map(({ slotId }) => slotId), context, ["visualOrderSlotIds"], "Visual slot order");
    exactSet(value.accessibleOrderSlotIds, value.slots
        .filter(({ accessibility }) => accessibility !== "ARTIFACT")
        .map(({ slotId }) => slotId), context, ["accessibleOrderSlotIds"], "Accessible slot order");
    const visualAccessibleProjection = value.visualOrderSlotIds.filter((slotId) => value.accessibleOrderSlotIds.includes(slotId));
    const sameOrder = visualAccessibleProjection.length === value.accessibleOrderSlotIds.length &&
        visualAccessibleProjection.every((slotId, index) => slotId === value.accessibleOrderSlotIds[index]);
    if (value.orderRelationshipPolicy === "MUST_DIFFER" && sameOrder)
        context.addIssue({
            code: "custom",
            path: ["orderRelationshipPolicy"],
            message: "This grammar requires materially distinct visual and accessible orders.",
        });
    if (value.orderRelationshipPolicy === "SAME_REQUIRED" && !sameOrder)
        context.addIssue({
            code: "custom",
            path: ["orderRelationshipPolicy"],
            message: "This grammar requires the same visual and accessible semantic order.",
        });
    unique(value.crossSpineSlotIds, context, ["crossSpineSlotIds"], "Cross-spine slots");
    unique(value.alternateGrammarIds, context, ["alternateGrammarIds"], "Alternate grammars");
    if (value.pageMode === "PAGE" && value.crossSpineSlotIds.length > 0)
        context.addIssue({
            code: "custom",
            path: ["crossSpineSlotIds"],
            message: "A one-page grammar cannot declare cross-spine slots.",
        });
    if (value.slots.some((slot) => value.pageMode === "PAGE"
        ? slot.physicalPageSide !== null
        : value.crossSpineSlotIds.includes(slot.slotId)
            ? slot.physicalPageSide !== null
            : slot.physicalPageSide === null))
        context.addIssue({
            code: "custom",
            path: ["slots"],
            message: "PAGE slots have no facing side; every non-cross-spine FACING_SPREAD slot must own exactly VERSO or RECTO, while cross-spine slots own neither.",
        });
    const slotById = new Map(value.slots.map((slot) => [slot.slotId, slot]));
    for (const crossSpineSlotId of value.crossSpineSlotIds) {
        const slot = slotById.get(crossSpineSlotId);
        if (!slot ||
            slot.nodeKind !== "MEDIA" ||
            slot.placementZone !== "BLEED" ||
            slot.coverageRequirement !== "COVER_ZONE_EXACT")
            context.addIssue({
                code: "custom",
                path: ["crossSpineSlotIds"],
                message: "Cross-spine slots must be exact bleed-media slots.",
            });
    }
    if (value.alternateGrammarIds.includes(value.grammarId))
        context.addIssue({
            code: "custom",
            path: ["alternateGrammarIds"],
            message: "A grammar cannot name itself as an alternate.",
        });
    for (const [slotIndex, slot] of value.slots.entries()) {
        unique(slot.implementationComponentIds, context, ["slots", slotIndex, "implementationComponentIds"], "Slot implementation components");
        unique(slot.acceptedContentKinds, context, ["slots", slotIndex], "Accepted content kinds");
        unique(slot.purposes, context, ["slots", slotIndex], "Slot purposes");
        unique(slot.overlapPermissions.map(({ counterpartSlotId }) => counterpartSlotId), context, ["slots", slotIndex, "overlapPermissions"], "Overlap counterparts");
        unique(slot.tokenApplications.map(({ token, targetProperty, componentId }) => `${token.tokenId}:${componentId}:${targetProperty}`), context, ["slots", slotIndex, "tokenApplications"], "Slot token applications");
        unique(slot.directionFlagBindings.map((binding) => exactDirectionFlagKey(binding)), context, ["slots", slotIndex, "directionFlagBindings"], "Slot direction-flag consumers");
        for (const application of slot.tokenApplications) {
            const expected = value.tokenBindings.find(({ tokenId }) => tokenId === application.token.tokenId);
            if (!expected ||
                expected.tokenClass !== application.token.tokenClass ||
                expected.valueDigestSha256 !== application.token.valueDigestSha256)
                context.addIssue({
                    code: "custom",
                    path: ["slots", slotIndex, "tokenApplications"],
                    message: "Every slot token application must bind an exact grammar token and value.",
                });
        }
        for (const flag of slot.directionFlagBindings)
            if (!value.requiredDirectionFlagBindings.some((required) => exactDirectionFlagKey(required) === exactDirectionFlagKey(flag)))
                context.addIssue({
                    code: "custom",
                    path: ["slots", slotIndex, "directionFlagBindings"],
                    message: "Every slot direction-flag use must bind an exact required consumer.",
                });
        for (const permission of slot.overlapPermissions) {
            const counterpart = slotById.get(permission.counterpartSlotId);
            const reciprocal = counterpart?.overlapPermissions.find(({ counterpartSlotId }) => counterpartSlotId === slot.slotId);
            if (!counterpart ||
                !reciprocal ||
                reciprocal.purpose !== permission.purpose ||
                JSON.stringify(reciprocal.policyBinding) !== JSON.stringify(permission.policyBinding))
                context.addIssue({
                    code: "custom",
                    path: ["slots", slotIndex, "overlapPermissions"],
                    message: "Overlap permissions must be reciprocal and purpose-identical.",
                });
        }
    }
    const backgroundApplication = value.surfaceBackgroundPolicy.sourceTokenApplication;
    if (backgroundApplication) {
        const expected = value.tokenBindings.find(({ tokenId }) => tokenId === backgroundApplication.token.tokenId);
        if (!expected ||
            expected.tokenClass !== backgroundApplication.token.tokenClass ||
            expected.valueDigestSha256 !== backgroundApplication.token.valueDigestSha256)
            context.addIssue({
                code: "custom",
                path: ["surfaceBackgroundPolicy"],
                message: "Surface background must consume one exact selected grammar token and value.",
            });
    }
    const appliedTokenIds = new Set([
        ...value.slots.flatMap(({ tokenApplications }) => tokenApplications.map(({ token }) => token.tokenId)),
        ...(backgroundApplication ? [backgroundApplication.token.tokenId] : []),
    ]);
    if (value.tokenBindings.some(({ tokenId }) => !appliedTokenIds.has(tokenId)))
        context.addIssue({
            code: "custom",
            path: ["tokenBindings"],
            message: "Every grammar token must have an exact slot-level consumer.",
        });
    const consumedFlagKeys = new Set(value.slots.flatMap(({ directionFlagBindings }) => directionFlagBindings.map((binding) => exactDirectionFlagKey(binding))));
    const consumedFlagList = value.slots.flatMap(({ directionFlagBindings }) => directionFlagBindings.map((binding) => exactDirectionFlagKey(binding)));
    unique(consumedFlagList, context, ["slots"], "Grammar direction-flag slot consumers");
    if (value.requiredDirectionFlagBindings.some((binding) => !consumedFlagKeys.has(exactDirectionFlagKey(binding))))
        context.addIssue({
            code: "custom",
            path: ["requiredDirectionFlagBindings"],
            message: "Every required direction-flag consumer must be assigned to a slot.",
        });
});
const attributionChannelPolicySchema = z.discriminatedUnion("channel", [
    z
        .object({
        channel: z.literal("LABEL"),
        policyBinding: bookProductionVersionBindingV1Schema,
        markerContentKinds: z.array(safeCodeSchema).min(1).max(40),
    })
        .strict(),
    z
        .object({
        channel: z.literal("POSITION"),
        policyBinding: bookProductionVersionBindingV1Schema,
        positionCodes: z.array(safeCodeSchema).min(1).max(40),
    })
        .strict(),
    z
        .object({
        channel: z.literal("TYPOGRAPHY"),
        policyBinding: bookProductionVersionBindingV1Schema,
        allowedComponentIds: z.array(opaqueIdSchema).min(1).max(40),
        allowedTargetProperties: z
            .array(z.enum([
            "TYPEFACE_FONT_ID",
            "FONT_SIZE_MILLI_POINTS",
            "LETTER_SPACING_MILLI_POINTS",
            "LINE_HEIGHT_MILLI_POINTS",
        ]))
            .min(1)
            .max(4),
    })
        .strict(),
    z
        .object({
        channel: z.literal("SYMBOL"),
        policyBinding: bookProductionVersionBindingV1Schema,
        allowedComponentIds: z.array(opaqueIdSchema).min(1).max(40),
    })
        .strict(),
]);
export const bookPageRolePolicyV1Schema = z
    .object({
    productPageRole: safeCodeSchema,
    pageMode: bookPageModeSchema,
    entryGrammarId: opaqueIdSchema,
    requiredPurposes: z.array(bookPageFunctionalPurposeSchema).min(1).max(10),
    requiredContentKinds: z.array(safeCodeSchema).max(100),
    requiredEvidence: z.array(bookPageRequiredEvidenceV1Schema).max(100),
    itemCountRules: z
        .array(z
        .object({
        contentKind: safeCodeSchema,
        mode: z.enum(["ZERO", "ONE_PER_NODE", "DECLARED_FROM_EVIDENCE"]),
        minimum: z.number().int().nonnegative().max(100_000),
        maximum: z.number().int().nonnegative().max(100_000),
        requiredFactCode: safeCodeSchema.nullable(),
        counterBinding: bookProductionVersionBindingV1Schema,
    })
        .strict()
        .refine(({ minimum, maximum }) => minimum <= maximum, "Item-count rule range must be ordered."))
        .min(1)
        .max(200),
    intentionalBlankPolicy: z
        .object({
        policyBinding: bookProductionVersionBindingV1Schema,
        reasonPolicies: z
            .array(z
            .object({
            reason: z.enum([
                "CHAPTER_VERSO",
                "SIGNATURE_PADDING",
                "ENDPAPER",
                "VENDOR_REQUIRED",
            ]),
            requiredEvidence: bookPageRequiredEvidenceV1Schema,
        })
            .strict())
            .min(1)
            .max(4),
        surfaceBackgroundPolicy: bookSurfaceBackgroundPolicyV1Schema,
        policyDigestSha256: sha256Schema,
    })
        .strict()
        .superRefine((value, context) => unique(value.reasonPolicies.map(({ reason }) => reason), context, ["reasonPolicies"], "Intentional-blank reasons"))
        .nullable(),
    authoredWhitespacePolicy: z
        .object({
        policyBinding: bookProductionVersionBindingV1Schema,
        authorizedGrammarIds: z.array(opaqueIdSchema).min(1).max(100),
        capacityProfileDigestSha256: sha256Schema,
        allowedPurposes: z.array(bookPageFunctionalPurposeSchema).min(1).max(10),
        maximumTextCharacterDeficit: z.number().int().positive().max(1_000_000),
        requiredEvidence: z.array(bookPageRequiredEvidenceV1Schema).min(1).max(40),
        requiredContextSignalIds: z.array(opaqueIdSchema).max(40),
    })
        .strict()
        .nullable(),
    spatialDensityPolicy: z
        .object({
        policyBinding: bookProductionVersionBindingV1Schema,
        authorizedGrammarIds: z.array(opaqueIdSchema).min(1).max(100),
        meaningfulPurposes: z.array(bookPageFunctionalPurposeSchema).min(1).max(10),
        minimumSafeAreaCoverageMillionths: z.number().int().positive().max(1_000_000),
        minimumVerticalSpanMillionths: z.number().int().positive().max(1_000_000),
        maximumLeadingVoidMillionths: z.number().int().nonnegative().max(999_999),
        maximumTrailingVoidMillionths: z.number().int().nonnegative().max(999_999),
    })
        .strict(),
    attributionPolicy: z
        .object({
        requiredSubjectIds: z.array(opaqueIdSchema).min(1).max(20),
        surfaceSubjectCoverage: z.enum(["ALL_REQUIRED_SUBJECTS", "AT_LEAST_ONE_REQUIRED_SUBJECT"]),
        attributedContentKinds: z.array(safeCodeSchema).min(1).max(100),
        allowedForms: z
            .array(z
            .object({
            voiceFormCode: safeCodeSchema,
            subjectIds: z.array(opaqueIdSchema).min(1).max(20),
            contentKinds: z.array(safeCodeSchema).min(1).max(100),
        })
            .strict())
            .min(1)
            .max(100),
        channelPolicies: z.array(attributionChannelPolicySchema).min(1).max(4),
        requireEveryTextNodeAttribution: z.boolean(),
        minimumNonColorChannelsPerTextNode: z.number().int().nonnegative().max(4),
        requiredNonColorChannelsPerTextNode: z
            .array(z.enum(["LABEL", "POSITION", "TYPOGRAPHY", "SYMBOL"]))
            .max(4),
    })
        .strict()
        .nullable(),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.requiredPurposes, context, ["requiredPurposes"], "Required role purposes");
    unique(value.requiredContentKinds, context, ["requiredContentKinds"], "Required content kinds");
    unique(value.spatialDensityPolicy.authorizedGrammarIds, context, ["spatialDensityPolicy", "authorizedGrammarIds"], "Spatial-density grammars");
    unique(value.spatialDensityPolicy.meaningfulPurposes, context, ["spatialDensityPolicy", "meaningfulPurposes"], "Spatial-density purposes");
    unique(value.itemCountRules.map(({ contentKind }) => contentKind), context, ["itemCountRules"], "Item-count content kinds");
    for (const [index, rule] of value.itemCountRules.entries()) {
        if ((rule.mode === "DECLARED_FROM_EVIDENCE") !== (rule.requiredFactCode !== null) ||
            (rule.mode === "ZERO" && (rule.minimum !== 0 || rule.maximum !== 0)) ||
            (rule.mode === "ONE_PER_NODE" && (rule.minimum !== 1 || rule.maximum !== 1)))
            context.addIssue({
                code: "custom",
                path: ["itemCountRules", index],
                message: "Item-count rule mode, range, and evidence fact must agree exactly.",
            });
        if (rule.mode === "DECLARED_FROM_EVIDENCE")
            context.addIssue({
                code: "custom",
                path: ["itemCountRules", index, "mode"],
                message: "DECLARED_FROM_EVIDENCE is fail-closed in P1 until an exact typed numeric counter observation is available.",
            });
    }
    unique(value.requiredEvidence.map(({ factCode }) => factCode), context, ["requiredEvidence"], "Required role evidence facts");
    if (value.authoredWhitespacePolicy) {
        unique(value.authoredWhitespacePolicy.authorizedGrammarIds, context, ["authoredWhitespacePolicy", "authorizedGrammarIds"], "Whitespace-authorized grammars");
        unique(value.authoredWhitespacePolicy.allowedPurposes, context, ["authoredWhitespacePolicy", "allowedPurposes"], "Whitespace purposes");
        unique(value.authoredWhitespacePolicy.requiredEvidence.map(({ factCode }) => factCode), context, ["authoredWhitespacePolicy", "requiredEvidence"], "Whitespace evidence facts");
        unique(value.authoredWhitespacePolicy.requiredContextSignalIds, context, ["authoredWhitespacePolicy", "requiredContextSignalIds"], "Whitespace context signals");
        if (!value.authoredWhitespacePolicy.requiredEvidence.some(({ allowedKinds }) => allowedKinds.some((kind) => kind !== "PRODUCT_POLICY")))
            context.addIssue({
                code: "custom",
                path: ["authoredWhitespacePolicy"],
                message: "Authored whitespace requires exact non-product-policy semantic evidence; a generic style signal is insufficient.",
            });
    }
    if (value.attributionPolicy) {
        unique(value.attributionPolicy.requiredSubjectIds, context, ["attributionPolicy", "requiredSubjectIds"], "Required attribution subjects");
        unique(value.attributionPolicy.attributedContentKinds, context, ["attributionPolicy", "attributedContentKinds"], "Attribution-applicable content kinds");
        unique(value.attributionPolicy.requiredNonColorChannelsPerTextNode, context, ["attributionPolicy", "requiredNonColorChannelsPerTextNode"], "Required non-color attribution channels");
        unique(value.attributionPolicy.channelPolicies.map(({ channel }) => channel), context, ["attributionPolicy", "channelPolicies"], "Attribution channel policies");
        for (const [index, channelPolicy] of value.attributionPolicy.channelPolicies.entries()) {
            if (channelPolicy.channel === "LABEL")
                unique(channelPolicy.markerContentKinds, context, ["attributionPolicy", "channelPolicies", index], "Label marker content kinds");
            if (channelPolicy.channel === "POSITION")
                unique(channelPolicy.positionCodes, context, ["attributionPolicy", "channelPolicies", index], "Attribution position codes");
            if (channelPolicy.channel === "TYPOGRAPHY") {
                unique(channelPolicy.allowedComponentIds, context, ["attributionPolicy", "channelPolicies", index], "Typography attribution components");
                unique(channelPolicy.allowedTargetProperties, context, ["attributionPolicy", "channelPolicies", index], "Typography attribution targets");
            }
            if (channelPolicy.channel === "SYMBOL")
                unique(channelPolicy.allowedComponentIds, context, ["attributionPolicy", "channelPolicies", index], "Symbol attribution components");
        }
        unique(value.attributionPolicy.allowedForms.map(({ voiceFormCode, subjectIds, contentKinds }) => `${voiceFormCode}:${[...subjectIds].sort().join(",")}:${[...contentKinds].sort().join(",")}`), context, ["attributionPolicy", "allowedForms"], "Allowed attribution forms");
        for (const [index, form] of value.attributionPolicy.allowedForms.entries()) {
            unique(form.subjectIds, context, ["attributionPolicy", "allowedForms", index, "subjectIds"], "Attribution-form subjects");
            unique(form.contentKinds, context, ["attributionPolicy", "allowedForms", index, "contentKinds"], "Attribution-form content kinds");
            if (form.subjectIds.some((subjectId) => !value.attributionPolicy?.requiredSubjectIds.includes(subjectId)))
                context.addIssue({
                    code: "custom",
                    path: ["attributionPolicy", "allowedForms", index, "subjectIds"],
                    message: "Attribution forms may use only the exact product-policy subject set.",
                });
        }
    }
});
export const bookAudienceAuthorizationPolicyV1Schema = z
    .object({
    authorizationPolicyId: opaqueIdSchema,
    disclosureClass: z.enum(["PRIVATE_EXACT_RECIPIENT_EDITION", "INTERNAL_RESTRICTED_REVIEW"]),
    policyBinding: bookProductionVersionBindingV1Schema,
    authorizationFieldIds: z.array(safeCodeSchema).min(1).max(40),
    recipientScopeFieldId: safeCodeSchema,
    consentPurposeCode: safeCodeSchema,
    requiredAuthorityKinds: z
        .array(z.enum(["PRINCIPAL", "GUARDIAN", "CONTRIBUTOR", "SOURCE_CUSTODIAN"]))
        .min(1)
        .max(4),
    authorizationPolicyDigestSha256: sha256Schema,
})
    .strict()
    .superRefine((value, context) => {
    unique(value.authorizationFieldIds, context, ["authorizationFieldIds"], "Audience authorization fields");
    if (!value.authorizationFieldIds.includes(value.recipientScopeFieldId))
        context.addIssue({
            code: "custom",
            path: ["recipientScopeFieldId"],
            message: "The exact recipient-scope authorization field must be one of the product-owned authorization fields.",
        });
    unique(value.requiredAuthorityKinds, context, ["requiredAuthorityKinds"], "Audience authority kinds");
});
export const bookPageComponentDefinitionV1Schema = z
    .object({
    componentId: opaqueIdSchema,
    componentDefinitionDigestSha256: sha256Schema,
    visualPlanComponentDigestSha256: sha256Schema,
    implementationBinding: bookProductionVersionBindingV1Schema,
    primitiveProgramBinding: bookProductionVersionBindingV1Schema,
    primitiveProgramDigestSha256: sha256Schema,
    primitiveCoordinateSpace: z.literal("NORMALIZED_MILLIONTHS"),
    styleOwnershipPolicy: z.literal("EVERY_COMMAND_FIXED_UNLESS_ONE_EXACT_RESOLVED_OVERRIDE"),
    primitiveCommands: z.array(bookComponentPrimitiveCommandV1Schema).min(1).max(500),
    classification: z.enum(["STRUCTURAL", "FUNCTIONAL", "DECORATIVE"]),
    purposes: z.array(bookPageFunctionalPurposeSchema).min(1).max(10),
    allowedProductPageRoles: z.array(safeCodeSchema).min(1).max(100),
    requiredEvidence: z.array(bookPageRequiredEvidenceV1Schema).max(100),
    requiredContextSignalIds: z.array(opaqueIdSchema).max(100),
    visibleFieldPolicies: z
        .array(z
        .object({
        fieldKind: safeCodeSchema,
        sourceClass: bookVisualFieldSourceClassSchema,
        allowedPresentations: z.array(bookVisualFieldPresentationSchema).min(1).max(4),
        requiredSemanticFact: safeCodeSchema.nullable(),
    })
        .strict())
        .max(40),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.purposes, context, ["purposes"], "Component purposes");
    unique(value.allowedProductPageRoles, context, ["allowedProductPageRoles"], "Component roles");
    unique(value.requiredEvidence.map(({ factCode }) => factCode), context, ["requiredEvidence"], "Component evidence facts");
    unique(value.requiredContextSignalIds, context, ["requiredContextSignalIds"], "Component context signals");
    unique(value.visibleFieldPolicies.map(({ fieldKind }) => fieldKind), context, ["visibleFieldPolicies"], "Component visible-field policies");
    unique(value.primitiveCommands.map(({ commandId }) => commandId), context, ["primitiveCommands"], "Component primitive commands");
    const orderedCommands = [...value.primitiveCommands].sort((left, right) => left.commandOrderIndex - right.commandOrderIndex);
    if (orderedCommands.some((command, index) => command.commandOrderIndex !== index))
        context.addIssue({
            code: "custom",
            path: ["primitiveCommands"],
            message: "Primitive command order must be unique and contiguous from zero.",
        });
    if (orderedCommands.some((command, index) => index > 0 && command.zIndex < orderedCommands[index - 1].zIndex))
        context.addIssue({
            code: "custom",
            path: ["primitiveCommands"],
            message: "Primitive command order is the exact render order and must be nondecreasing by local z-index.",
        });
    const commandById = new Map(value.primitiveCommands.map((command) => [command.commandId, command]));
    for (const [index, command] of value.primitiveCommands.entries()) {
        if ("targetCommandId" in command &&
            ((command.commandKind.startsWith("LIVE_TEXT_") &&
                commandById.get(command.targetCommandId)?.commandKind !== "LIVE_TEXT_REFERENCE") ||
                (command.commandKind === "MEDIA_TREATMENT" &&
                    commandById.get(command.targetCommandId)?.commandKind !== "GOVERNED_MEDIA_REFERENCE")))
            context.addIssue({
                code: "custom",
                path: ["primitiveCommands", index, "targetCommandId"],
                message: "Every component style command must target one exact compatible field or media template command.",
            });
    }
    for (const reference of value.primitiveCommands) {
        if (reference.commandKind === "LIVE_TEXT_REFERENCE") {
            for (const requiredKind of [
                "LIVE_TEXT_PAINT",
                "LIVE_TEXT_TYPOGRAPHY",
                "LIVE_TEXT_FLOW",
            ]) {
                const writers = value.primitiveCommands.filter((command) => command.commandKind === requiredKind &&
                    command.targetCommandId === reference.commandId);
                if (writers.length !== 1)
                    context.addIssue({
                        code: "custom",
                        path: ["primitiveCommands"],
                        message: `Every live-text reference requires exactly one final ${requiredKind} writer.`,
                    });
            }
        }
        if (reference.commandKind === "GOVERNED_MEDIA_REFERENCE") {
            const writers = value.primitiveCommands.filter((command) => command.commandKind === "MEDIA_TREATMENT" &&
                command.targetCommandId === reference.commandId);
            if (writers.length !== 1)
                context.addIssue({
                    code: "custom",
                    path: ["primitiveCommands"],
                    message: "Every governed-media reference requires exactly one final treatment writer.",
                });
        }
    }
    if (value.purposes.includes("NAVIGATION") &&
        !value.primitiveCommands.some((command) => command.commandKind === "LIVE_TEXT_REFERENCE" && command.navigationTargetRole !== null))
        context.addIssue({
            code: "custom",
            path: ["primitiveCommands"],
            message: "Navigation components require an exact live marker reference; vector/rule ornament alone is not navigation.",
        });
    if (value.classification === "DECORATIVE" &&
        value.requiredEvidence.length === 0 &&
        value.requiredContextSignalIds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["classification"],
            message: "Decorative components require exact supplied evidence or policy-bound supplied context grounding.",
        });
    if (value.classification === "DECORATIVE" &&
        value.visibleFieldPolicies.some(({ requiredSemanticFact }) => requiredSemanticFact === null))
        context.addIssue({
            code: "custom",
            path: ["visibleFieldPolicies"],
            message: "Every customer-visible field on a decorative component requires an exact semantic fact.",
        });
    if (value.classification === "DECORATIVE" &&
        value.requiredContextSignalIds.length === 0 &&
        !value.requiredEvidence.some(({ allowedKinds }) => allowedKinds.some((kind) => kind !== "PRODUCT_POLICY")))
        context.addIssue({
            code: "custom",
            path: ["requiredEvidence"],
            message: "Decorative components cannot be grounded only by generic product-policy evidence.",
        });
    for (const [index, field] of value.visibleFieldPolicies.entries()) {
        if (field.sourceClass === "INTERNAL_IDENTIFIER" &&
            field.allowedPresentations.some((presentation) => presentation !== "OMITTED"))
            context.addIssue({
                code: "custom",
                path: ["visibleFieldPolicies", index, "allowedPresentations"],
                message: "Internal identifiers are never customer-visible in P1 print composition.",
            });
        if (field.requiredSemanticFact !== null &&
            !value.requiredEvidence.some(({ factCode }) => factCode === field.requiredSemanticFact))
            context.addIssue({
                code: "custom",
                path: ["visibleFieldPolicies", index, "requiredSemanticFact"],
                message: "A visible semantic field must resolve through the component's exact required evidence policy.",
            });
        if ((value.purposes.some((purpose) => ["CHRONOLOGY", "EVIDENCE", "SAFETY", "ATTRIBUTION", "VOICE"].includes(purpose)) ||
            (value.purposes.includes("NAVIGATION") && field.sourceClass !== "INTERNAL_IDENTIFIER")) &&
            field.requiredSemanticFact === null)
            context.addIssue({
                code: "custom",
                path: ["visibleFieldPolicies", index, "requiredSemanticFact"],
                message: "Semantically labeled chronology, evidence, safety, attribution, voice, or content-derived navigation fields require an exact fact.",
            });
    }
});
const grammarCatalogBodySchema = z
    .object({
    contract: bookPageGrammarCatalogContractSchema,
    catalogId: opaqueIdSchema,
    product: bookProductionProductSchema,
    directionId: opaqueIdSchema,
    creationBriefDigestSha256: sha256Schema,
    visualIntentDigestSha256: sha256Schema,
    directionKitId: opaqueIdSchema,
    directionKitDigestSha256: sha256Schema,
    directionKitSelectionDigestSha256: sha256Schema,
    visualImplementationPlanId: opaqueIdSchema,
    visualImplementationPlanDigestSha256: sha256Schema,
    rendererBinding: bookProductionVersionBindingV1Schema,
    configurationBinding: bookProductionVersionBindingV1Schema,
    outputProfileBinding: bookProductionVersionBindingV1Schema,
    fontRegistryBinding: bookProductionVersionBindingV1Schema,
    productPolicyBinding: bookProductionVersionBindingV1Schema,
    primitiveFingerprintPolicyBinding: bookProductionVersionBindingV1Schema,
    sourceFingerprintDigestSha256: sha256Schema,
    primitiveFingerprintDigestSha256: sha256Schema,
    visibleStyleSignatureDigestSha256: sha256Schema,
    geometryId: opaqueIdSchema,
    geometryDigestSha256: sha256Schema,
    fontRegistryId: opaqueIdSchema,
    fontRegistryDigestSha256: sha256Schema,
    audienceAuthorizationPolicies: z.array(bookAudienceAuthorizationPolicyV1Schema).max(10),
    roles: z.array(bookPageRolePolicyV1Schema).min(1).max(200),
    components: z.array(bookPageComponentDefinitionV1Schema).max(500),
    grammars: z.array(bookPageGrammarV1Schema).min(1).max(500),
    truthBoundary: z.literal("SUPPLIED_PRODUCT_POLICY_REFINEMENT_NOT_AUTHENTICATED"),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.roles.map(({ productPageRole }) => productPageRole), context, ["roles"], "Roles");
    unique(value.audienceAuthorizationPolicies.map(({ authorizationPolicyId }) => authorizationPolicyId), context, ["audienceAuthorizationPolicies"], "Audience authorization policies");
    unique(value.audienceAuthorizationPolicies.map(({ disclosureClass }) => disclosureClass), context, ["audienceAuthorizationPolicies"], "Audience disclosure classes");
    unique(value.components.map(({ componentId }) => componentId), context, ["components"], "Components");
    unique(value.grammars.map(({ grammarId }) => grammarId), context, ["grammars"], "Grammars");
    const roleById = new Map(value.roles.map((role) => [role.productPageRole, role]));
    const grammarById = new Map(value.grammars.map((grammar) => [grammar.grammarId, grammar]));
    const componentById = new Map(value.components.map((component) => [component.componentId, component]));
    for (const [index, role] of value.roles.entries()) {
        const grammar = grammarById.get(role.entryGrammarId);
        if (!grammar ||
            grammar.productPageRole !== role.productPageRole ||
            grammar.pageMode !== role.pageMode)
            context.addIssue({
                code: "custom",
                path: ["roles", index, "entryGrammarId"],
                message: "A role entry grammar must implement the exact role and surface mode.",
            });
        if (role.authoredWhitespacePolicy &&
            JSON.stringify(role.authoredWhitespacePolicy.policyBinding) !==
                JSON.stringify(value.productPolicyBinding))
            context.addIssue({
                code: "custom",
                path: ["roles", index, "authoredWhitespacePolicy", "policyBinding"],
                message: "Authored whitespace must bind the exact selected product-policy version.",
            });
        if (role.authoredWhitespacePolicy)
            for (const grammarId of role.authoredWhitespacePolicy.authorizedGrammarIds) {
                const authorized = grammarById.get(grammarId);
                if (!authorized || authorized.productPageRole !== role.productPageRole)
                    context.addIssue({
                        code: "custom",
                        path: ["roles", index, "authoredWhitespacePolicy", "authorizedGrammarIds"],
                        message: "Whitespace authorization must name exact grammars for this role.",
                    });
            }
    }
    for (const [index, grammar] of value.grammars.entries()) {
        if (!roleById.has(grammar.productPageRole))
            context.addIssue({
                code: "custom",
                path: ["grammars", index, "productPageRole"],
                message: "Grammar references an unknown product-owned role.",
            });
        const role = roleById.get(grammar.productPageRole);
        if (grammar.underfillAction === "ALLOW_AUTHORED_WHITESPACE" &&
            !role?.authoredWhitespacePolicy)
            context.addIssue({
                code: "custom",
                path: ["grammars", index, "underfillAction"],
                message: "Authored whitespace requires an exact role-specific semantic authorization policy.",
            });
        if (grammar.underfillAction === "ALLOW_AUTHORED_WHITESPACE" &&
            role?.authoredWhitespacePolicy &&
            !role.authoredWhitespacePolicy.authorizedGrammarIds.includes(grammar.grammarId))
            context.addIssue({
                code: "custom",
                path: ["grammars", index, "underfillAction"],
                message: "Authored whitespace must name this exact grammar, not only its role.",
            });
        if ((value.product === "MY_STORY" || value.product === "OUR_STORY") &&
            grammar.slots.some(({ nodeKind }) => nodeKind === "TEXT") &&
            (!role?.attributionPolicy || !role.attributionPolicy.requireEveryTextNodeAttribution))
            context.addIssue({
                code: "custom",
                path: ["grammars", index, "purposes"],
                message: "Every narrative role with live text requires an exact product-owned attribution policy with mandatory attribution for its declared voice-bearing kinds.",
            });
        if (role?.attributionPolicy)
            for (const [slotIndex, slot] of grammar.slots.entries())
                if (slot.nodeKind === "TEXT" &&
                    slot.purposes.some((purpose) => purpose === "VOICE" || purpose === "ATTRIBUTION") &&
                    slot.acceptedContentKinds.some((contentKind) => !role.attributionPolicy?.attributedContentKinds.includes(contentKind)))
                    context.addIssue({
                        code: "custom",
                        path: ["grammars", index, "slots", slotIndex, "acceptedContentKinds"],
                        message: "Every voice-bearing text content kind must be covered by the role attribution policy.",
                    });
        for (const alternateId of grammar.alternateGrammarIds) {
            const alternate = grammarById.get(alternateId);
            if (!alternate ||
                alternate.productPageRole !== grammar.productPageRole ||
                alternate.pageMode !== grammar.pageMode)
                context.addIssue({
                    code: "custom",
                    path: ["grammars", index, "alternateGrammarIds"],
                    message: "Alternate grammar must implement the exact same role and surface mode.",
                });
        }
        const allSlotPurposes = new Set(grammar.slots.flatMap(({ purposes }) => purposes));
        if (grammar.purposes.some((purpose) => !allSlotPurposes.has(purpose)) ||
            [...allSlotPurposes].some((purpose) => !grammar.purposes.includes(purpose)))
            context.addIssue({
                code: "custom",
                path: ["grammars", index, "purposes"],
                message: "Grammar purposes must exactly equal the functional purposes implemented by its slots.",
            });
        for (const [slotIndex, slot] of grammar.slots.entries()) {
            if (slot.purposes.some((purpose) => !grammar.purposes.includes(purpose)))
                context.addIssue({
                    code: "custom",
                    path: ["grammars", index, "slots", slotIndex, "purposes"],
                    message: "Slot purposes must be a semantic subset of the grammar purposes.",
                });
            for (const componentId of slot.implementationComponentIds) {
                const component = componentById.get(componentId);
                if (!component ||
                    !component.allowedProductPageRoles.includes(grammar.productPageRole) ||
                    !slot.purposes.some((purpose) => component.purposes.includes(purpose)))
                    context.addIssue({
                        code: "custom",
                        path: ["grammars", index, "slots", slotIndex, "implementationComponentIds"],
                        message: `Implementation component ${componentId} does not implement this role and at least one exact slot purpose.`,
                    });
            }
            for (const purpose of slot.purposes)
                if (!slot.implementationComponentIds.some((componentId) => componentById.get(componentId)?.purposes.includes(purpose)))
                    context.addIssue({
                        code: "custom",
                        path: ["grammars", index, "slots", slotIndex, "purposes"],
                        message: `Slot purpose ${purpose} has no exact implementation component.`,
                    });
            if (slot.nodeKind === "COMPONENT")
                for (const componentId of slot.allowedComponentIds) {
                    const component = componentById.get(componentId);
                    if (!component || !component.allowedProductPageRoles.includes(grammar.productPageRole))
                        context.addIssue({
                            code: "custom",
                            path: ["grammars", index, "slots"],
                            message: `Component ${componentId} is not defined for ${grammar.productPageRole}.`,
                        });
                }
        }
    }
    const cycleVisitState = new Map();
    const hasCycle = (grammarId) => {
        const state = cycleVisitState.get(grammarId);
        if (state === "ACTIVE")
            return true;
        if (state === "COMPLETE")
            return false;
        cycleVisitState.set(grammarId, "ACTIVE");
        const grammar = grammarById.get(grammarId);
        const cyclic = Boolean(grammar?.alternateGrammarIds.some(hasCycle));
        cycleVisitState.set(grammarId, "COMPLETE");
        return cyclic;
    };
    if (value.grammars.some(({ grammarId }) => hasCycle(grammarId)))
        context.addIssue({
            code: "custom",
            path: ["grammars"],
            message: "Alternate-grammar graphs must be acyclic.",
        });
});
export const bookPageGrammarCatalogV1Schema = grammarCatalogBodySchema.safeExtend({
    catalogDigestSha256: sha256Schema,
});
const nodeBase = {
    nodeId: opaqueIdSchema,
    contentKind: safeCodeSchema,
    productItemCountContribution: z.number().int().nonnegative().max(100_000),
    itemCountEvidenceId: opaqueIdSchema.nullable(),
    capacityPolicyBinding: bookProductionVersionBindingV1Schema,
    evidenceIds: z.array(opaqueIdSchema).max(100),
    contextSignalIds: z.array(opaqueIdSchema).max(100),
    nodeDigestSha256: sha256Schema,
};
const attributionChannelBindingSchema = z.discriminatedUnion("channel", [
    z
        .object({
        channel: z.literal("LABEL"),
        policyBinding: bookProductionVersionBindingV1Schema,
        markerNodeId: opaqueIdSchema,
        markerRendererInputMacSha256: sha256Schema,
    })
        .strict(),
    z
        .object({
        channel: z.literal("POSITION"),
        policyBinding: bookProductionVersionBindingV1Schema,
        positionCode: safeCodeSchema,
    })
        .strict(),
    z
        .object({
        channel: z.literal("TYPOGRAPHY"),
        policyBinding: bookProductionVersionBindingV1Schema,
        componentId: opaqueIdSchema,
        targetProperty: z.enum([
            "TYPEFACE_FONT_ID",
            "FONT_SIZE_MILLI_POINTS",
            "LETTER_SPACING_MILLI_POINTS",
            "LINE_HEIGHT_MILLI_POINTS",
        ]),
        sourceApplicationDigestSha256: sha256Schema,
    })
        .strict(),
    z
        .object({
        channel: z.literal("SYMBOL"),
        policyBinding: bookProductionVersionBindingV1Schema,
        componentNodeId: opaqueIdSchema,
        componentId: opaqueIdSchema,
    })
        .strict(),
]);
const textNodeSchema = z
    .object({
    ...nodeBase,
    nodeKind: z.literal("TEXT"),
    textResource: bookProtectedTextResourceBindingV1Schema,
    fallbackChainId: opaqueIdSchema,
    preferredFontId: opaqueIdSchema,
    script: safeCodeSchema,
    languageTag: localeSchema,
    direction: z.enum(["LEFT_TO_RIGHT", "RIGHT_TO_LEFT", "TOP_TO_BOTTOM"]),
    bidiPolicy: z.literal("EXPLICIT_DIRECTION_NO_PLATFORM_AUTODETECT"),
    normalizationPolicy: z.literal("NFC"),
    controlPolicy: bookTextControlPolicyV1Schema,
    requestedFontSizeMilliPoints: z.number().int().positive().max(1_000_000),
    minimumFontSizeMilliPoints: z.number().int().positive().max(1_000_000),
    lineHeightMilliPoints: z.number().int().positive().max(2_000_000),
    letterSpacingMilliPoints: coordinateSchema,
    textFlowPolicy: bookTextFlowPolicyV1Schema,
    attribution: z
        .object({
        subjectIds: z.array(opaqueIdSchema).min(1).max(20),
        voiceFormCode: safeCodeSchema,
        channelBindings: z.array(attributionChannelBindingSchema).min(1).max(4),
    })
        .strict()
        .nullable(),
    componentVisibleFieldUse: z
        .object({
        componentNodeId: opaqueIdSchema,
        componentId: opaqueIdSchema,
        fieldKind: safeCodeSchema,
        sourceClass: bookVisualFieldSourceClassSchema,
        presentation: bookVisualFieldPresentationSchema,
        sourceAuthorityEvidenceId: opaqueIdSchema,
        semanticFactEvidenceId: opaqueIdSchema.nullable(),
    })
        .strict()
        .nullable(),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.evidenceIds, context, ["evidenceIds"], "Text evidence IDs");
    unique(value.contextSignalIds, context, ["contextSignalIds"], "Text context signals");
    unique(value.controlPolicy.allowedBidiControlCodePoints.map(String), context, ["controlPolicy", "allowedBidiControlCodePoints"], "Allowed bidi controls");
    const bidiControls = new Set([
        0x061c, 0x200e, 0x200f, 0x202a, 0x202b, 0x202c, 0x202d, 0x202e, 0x2066, 0x2067, 0x2068,
        0x2069,
    ]);
    if (value.controlPolicy.allowedBidiControlCodePoints.some((point) => !bidiControls.has(point)))
        context.addIssue({
            code: "custom",
            path: ["controlPolicy", "allowedBidiControlCodePoints"],
            message: "Only actual Unicode bidi-control code points may be allowlisted.",
        });
    if (value.requestedFontSizeMilliPoints < value.minimumFontSizeMilliPoints)
        context.addIssue({
            code: "custom",
            path: ["requestedFontSizeMilliPoints"],
            message: "Requested live type cannot start below its declared minimum size.",
        });
    if (value.attribution) {
        unique(value.attribution.subjectIds, context, ["attribution", "subjectIds"], "Attribution subjects");
        unique(value.attribution.channelBindings.map(({ channel }) => channel), context, ["attribution", "channelBindings"], "Attribution channels");
    }
});
const mediaNodeSchema = z
    .object({
    ...nodeBase,
    nodeKind: z.literal("MEDIA"),
    assetUseBinding: bookPublishingAssetUseExactBindingV1Schema,
    pixelWidth: z.number().int().positive().max(1_000_000),
    pixelHeight: z.number().int().positive().max(1_000_000),
    fitMode: z.enum(["EXACT_CROP_FILL", "CONTAIN", "EXACT_PIXEL_SIZE"]),
    sourceCropPixels: z
        .object({
        x: z.number().int().nonnegative().max(1_000_000),
        y: z.number().int().nonnegative().max(1_000_000),
        width: z.number().int().positive().max(1_000_000),
        height: z.number().int().positive().max(1_000_000),
    })
        .strict(),
    contextualAltBinding: z
        .object({
        contextualAltDigestSha256: sha256Schema,
        contextualAltSourceClass: z.enum([
            "BOOK_CONTEXT_AUTHORED",
            "CUSTOMER_APPROVED",
            "EDITORIAL_SEMANTIC_DERIVATION",
        ]),
        contextualAltSourceBindingDigestSha256: sha256Schema,
        contextualAltResourceBindingDigestSha256: sha256Schema,
    })
        .strict()
        .nullable(),
    alternateDescriptionResource: bookProtectedTextResourceBindingV1Schema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.evidenceIds, context, ["evidenceIds"], "Media evidence IDs");
    unique(value.contextSignalIds, context, ["contextSignalIds"], "Media context signals");
    if (value.sourceCropPixels.x + value.sourceCropPixels.width > value.pixelWidth ||
        value.sourceCropPixels.y + value.sourceCropPixels.height > value.pixelHeight)
        context.addIssue({
            code: "custom",
            path: ["sourceCropPixels"],
            message: "Media crop must stay inside exact source pixel dimensions.",
        });
    if ((value.contextualAltBinding === null) !== (value.alternateDescriptionResource === null))
        context.addIssue({
            code: "custom",
            path: ["alternateDescriptionResource"],
            message: "Contextual alternate-description binding and protected resource must coexist.",
        });
});
const componentNodeSchema = z
    .object({
    ...nodeBase,
    nodeKind: z.literal("COMPONENT"),
    componentId: opaqueIdSchema,
    componentDefinitionDigestSha256: sha256Schema,
    visibleFieldNodeIds: z.array(opaqueIdSchema).max(40),
    navigationBindings: z
        .array(z
        .object({
        navigationTargetRole: safeCodeSchema,
        fieldKind: safeCodeSchema,
        targetKind: z.enum(["SURFACE", "PAGE"]),
        targetId: opaqueIdSchema,
        orderIndex: z.number().int().nonnegative().max(10_000),
    })
        .strict())
        .max(40),
    governedMediaNodeBindings: z
        .array(z
        .object({
        assetRoleCode: safeCodeSchema,
        mediaNodeId: opaqueIdSchema,
    })
        .strict())
        .max(40),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.evidenceIds, context, ["evidenceIds"], "Component evidence IDs");
    unique(value.contextSignalIds, context, ["contextSignalIds"], "Component context signals");
    unique(value.visibleFieldNodeIds, context, ["visibleFieldNodeIds"], "Component visible-field nodes");
    unique(value.navigationBindings.map(({ navigationTargetRole }) => navigationTargetRole), context, ["navigationBindings"], "Component navigation target roles");
    unique(value.navigationBindings.map(({ orderIndex }) => String(orderIndex)), context, ["navigationBindings"], "Component navigation order indices");
    if ([...value.navigationBindings]
        .sort((left, right) => left.orderIndex - right.orderIndex)
        .some(({ orderIndex }, index) => orderIndex !== index))
        context.addIssue({
            code: "custom",
            path: ["navigationBindings"],
            message: "Component navigation bindings must use exact contiguous document-order indices from zero.",
        });
    unique(value.governedMediaNodeBindings.map(({ assetRoleCode }) => assetRoleCode), context, ["governedMediaNodeBindings"], "Component governed-media roles");
    unique(value.governedMediaNodeBindings.map(({ mediaNodeId }) => mediaNodeId), context, ["governedMediaNodeBindings"], "Component governed-media nodes");
});
export const bookPageSemanticNodeV1Schema = z.discriminatedUnion("nodeKind", [
    textNodeSchema,
    mediaNodeSchema,
    componentNodeSchema,
]);
export const bookPhysicalPageRefV1Schema = z
    .object({
    pageId: opaqueIdSchema,
    productionOrdinal: z.number().int().positive().max(2_000),
    side: bookPageSideSchema,
})
    .strict();
export const bookOutputAudiencePolicyV1Schema = z
    .object({
    disclosureClass: z.enum([
        "PUBLIC_SAMPLE",
        "PRIVATE_EXACT_RECIPIENT_EDITION",
        "INTERNAL_RESTRICTED_REVIEW",
    ]),
    revisionId: opaqueIdSchema,
    outputArtifactRole: safeCodeSchema,
    policyBinding: bookProductionVersionBindingV1Schema,
    authorizationPolicyId: opaqueIdSchema.nullable(),
    authorizationPolicyDigestSha256: sha256Schema.nullable(),
    authorizingAuthoritySubjectIds: z.array(opaqueIdSchema).max(40),
    intendedRecipientBindings: z
        .array(z
        .object({
        recipientBindingId: opaqueIdSchema,
        revision: z.number().int().positive(),
        identityDigestSha256: sha256Schema,
    })
        .strict())
        .max(40),
    authorizationDecisionIds: z.array(opaqueIdSchema).max(100),
    authorizedContentDecisionIds: z.array(opaqueIdSchema).max(500),
    recipientScopeDigestSha256: sha256Schema,
    authorizationDigestSha256: sha256Schema,
    truthBoundary: z.literal("SUPPLIED_AUDIENCE_POLICY_REQUIRES_TRUSTED_IDENTITY_ADAPTER"),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.authorizingAuthoritySubjectIds, context, ["authorizingAuthoritySubjectIds"], "Audience-authorizing subjects");
    unique(value.intendedRecipientBindings.map(({ recipientBindingId }) => recipientBindingId), context, ["intendedRecipientBindings"], "Intended recipient bindings");
    unique(value.authorizationDecisionIds, context, ["authorizationDecisionIds"], "Audience authorization decisions");
    unique(value.authorizedContentDecisionIds, context, ["authorizedContentDecisionIds"], "Audience-authorized content decisions");
    if (value.disclosureClass === "PUBLIC_SAMPLE" &&
        (value.authorizingAuthoritySubjectIds.length > 0 ||
            value.intendedRecipientBindings.length > 0 ||
            value.authorizationDecisionIds.length > 0 ||
            value.authorizedContentDecisionIds.length > 0 ||
            value.authorizationPolicyId !== null ||
            value.authorizationPolicyDigestSha256 !== null))
        context.addIssue({
            code: "custom",
            path: ["disclosureClass"],
            message: "A public sample cannot carry private recipient or authorization identities.",
        });
    if (value.disclosureClass !== "PUBLIC_SAMPLE" &&
        (value.authorizingAuthoritySubjectIds.length === 0 ||
            value.intendedRecipientBindings.length === 0 ||
            value.authorizationDecisionIds.length === 0 ||
            value.authorizationPolicyId === null ||
            value.authorizationPolicyDigestSha256 === null))
        context.addIssue({
            code: "custom",
            path: ["authorizationDecisionIds"],
            message: "Private and restricted editions require exact supplied recipient subjects and authorization decisions.",
        });
});
export const bookSemanticCompositionSurfaceV1Schema = z
    .object({
    surfaceId: opaqueIdSchema,
    surfaceMode: bookPageModeSchema,
    physicalPages: z.array(bookPhysicalPageRefV1Schema).min(1).max(2),
    productPageRole: safeCodeSchema,
    surfaceDisposition: z.enum(["POPULATED", "INTENTIONAL_PRODUCTION_BLANK"]),
    intentionalBlankAuthorization: z
        .object({
        reason: z.enum(["CHAPTER_VERSO", "SIGNATURE_PADDING", "ENDPAPER", "VENDOR_REQUIRED"]),
        policyBinding: bookProductionVersionBindingV1Schema,
        policyDigestSha256: sha256Schema,
        evidenceId: opaqueIdSchema,
        authorizationDigestSha256: sha256Schema,
    })
        .strict()
        .nullable(),
    requestedGrammarId: opaqueIdSchema.nullable(),
    evidenceBindings: z.array(bookPageSemanticEvidenceBindingV1Schema).max(200),
    contextSignalBindings: z.array(bookPageContextSignalBindingV1Schema).max(200),
    nodes: z.array(bookPageSemanticNodeV1Schema).max(500),
    crossSpineNodeIds: z.array(opaqueIdSchema).max(100),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.physicalPages.map(({ pageId }) => pageId), context, ["physicalPages"], "Page IDs");
    unique(value.nodes.map(({ nodeId }) => nodeId), context, ["nodes"], "Surface node IDs");
    unique(value.evidenceBindings.map(({ evidenceId }) => evidenceId), context, ["evidenceBindings"], "Surface evidence IDs");
    unique(value.contextSignalBindings.map(({ signalId }) => signalId), context, ["contextSignalBindings"], "Surface context signal IDs");
    unique(value.crossSpineNodeIds, context, ["crossSpineNodeIds"], "Cross-spine node IDs");
    if (value.surfaceDisposition === "POPULATED" &&
        (value.nodes.length === 0 || value.intentionalBlankAuthorization !== null))
        context.addIssue({
            code: "custom",
            path: ["surfaceDisposition"],
            message: "A populated surface requires semantic nodes and cannot carry blank authorization.",
        });
    if (value.surfaceDisposition === "INTENTIONAL_PRODUCTION_BLANK" &&
        (value.nodes.length > 0 ||
            value.crossSpineNodeIds.length > 0 ||
            value.requestedGrammarId !== null ||
            value.intentionalBlankAuthorization === null ||
            value.surfaceMode !== "PAGE"))
        context.addIssue({
            code: "custom",
            path: ["surfaceDisposition"],
            message: "An intentional production blank requires exact authorization, PAGE topology, no grammar request, no semantic nodes, and no cross-spine content.",
        });
    if (value.surfaceMode === "PAGE" && value.physicalPages.length !== 1)
        context.addIssue({
            code: "custom",
            path: ["physicalPages"],
            message: "A PAGE surface must own exactly one physical page.",
        });
    if (value.surfaceMode === "PAGE" && value.crossSpineNodeIds.length > 0)
        context.addIssue({
            code: "custom",
            path: ["crossSpineNodeIds"],
            message: "A PAGE surface cannot contain cross-spine nodes.",
        });
    if (value.surfaceMode === "FACING_SPREAD") {
        const [verso, recto] = value.physicalPages;
        if (value.physicalPages.length !== 2 ||
            verso?.side !== "VERSO" ||
            recto?.side !== "RECTO" ||
            recto.productionOrdinal !== verso.productionOrdinal + 1)
            context.addIssue({
                code: "custom",
                path: ["physicalPages"],
                message: "A facing spread requires ordered adjacent verso and recto page identities.",
            });
        const nodeById = new Map(value.nodes.map((node) => [node.nodeId, node]));
        for (const nodeId of value.crossSpineNodeIds)
            if (nodeById.get(nodeId)?.nodeKind !== "MEDIA")
                context.addIssue({
                    code: "custom",
                    path: ["crossSpineNodeIds"],
                    message: "Only logical media nodes may be declared cross-spine.",
                });
    }
});
const compositionRequestBodySchema = z
    .object({
    contract: bookPageCompositionRequestContractSchema,
    requestId: opaqueIdSchema,
    bookId: opaqueIdSchema,
    revisionId: opaqueIdSchema,
    product: bookProductionProductSchema,
    outputAudiencePolicy: bookOutputAudiencePolicyV1Schema,
    creationSourceBinding: bookGenerationSourceBindingV1Schema,
    runtimeSourceSnapshotId: opaqueIdSchema,
    runtimeSourceArtifact: bookProductionArtifactBindingV1Schema,
    creationBriefDigestSha256: sha256Schema,
    visualIntentDigestSha256: sha256Schema,
    directionKitId: opaqueIdSchema,
    directionKitDigestSha256: sha256Schema,
    directionKitSelectionDigestSha256: sha256Schema,
    visualImplementationPlanId: opaqueIdSchema,
    visualImplementationPlanDigestSha256: sha256Schema,
    sourceFingerprintDigestSha256: sha256Schema,
    realizedVisualSignatureDigestSha256: sha256Schema,
    visibleStyleSignatureDigestSha256: sha256Schema,
    catalogId: opaqueIdSchema,
    catalogDigestSha256: sha256Schema,
    geometryId: opaqueIdSchema,
    geometryDigestSha256: sha256Schema,
    fontRegistryId: opaqueIdSchema,
    fontRegistryDigestSha256: sha256Schema,
    constraintObservations: z.array(bookPageConstraintObservationV1Schema).max(300),
    surfaces: z.array(bookSemanticCompositionSurfaceV1Schema).min(1).max(2_000),
    truthBoundary: z.literal("SUPPLIED_SEMANTIC_RESOURCES_NOT_AUTHENTICATED"),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.constraintObservations.map(({ constraintId }) => constraintId), context, ["constraintObservations"], "Constraint observations");
    unique(value.surfaces.map(({ surfaceId }) => surfaceId), context, ["surfaces"], "Surface IDs");
    unique(value.surfaces.flatMap(({ physicalPages }) => physicalPages.map(({ pageId }) => pageId)), context, ["surfaces"], "Physical page IDs");
    unique(value.surfaces.flatMap(({ nodes }) => nodes.map(({ nodeId }) => nodeId)), context, ["surfaces"], "Global semantic node IDs");
    const ordinals = value.surfaces.flatMap(({ physicalPages }) => physicalPages.map(({ productionOrdinal }) => productionOrdinal));
    if (ordinals.some((ordinal, index) => ordinal !== index + 1))
        context.addIssue({
            code: "custom",
            path: ["surfaces"],
            message: "Physical pages must appear exactly once in canonical ordinal order starting at one.",
        });
});
export const bookPageCompositionRequestV1Schema = compositionRequestBodySchema.safeExtend({
    requestDigestSha256: sha256Schema,
});
export const bookPageFrameV1Schema = z
    .object({
    pageId: opaqueIdSchema,
    productionOrdinal: z.number().int().positive().max(2_000),
    side: bookPageSideSchema,
    surfaceOffsetXMilliPoints: coordinateSchema,
    bleedBox: bookPageRectV1Schema,
    trimBox: bookPageRectV1Schema,
    safeContentBox: bookPageRectV1Schema,
    bindingExclusionBox: bookPageRectV1Schema.nullable(),
})
    .strict();
export const bookPagePlacementSegmentV1Schema = z
    .object({
    pageId: opaqueIdSchema,
    destinationRectOnPage: bookPageRectV1Schema,
    sourceRectInPlacement: bookPageRectV1Schema,
})
    .strict();
export const bookPageResolvedPlacementV1Schema = z
    .object({
    placementId: opaqueIdSchema,
    nodeId: opaqueIdSchema,
    nodeDigestSha256: sha256Schema,
    nodeKind: z.enum(["TEXT", "MEDIA", "COMPONENT"]),
    contentKind: safeCodeSchema,
    slotId: opaqueIdSchema,
    implementationComponentIds: z.array(opaqueIdSchema).min(1).max(100),
    surfaceRect: bookPageRectV1Schema,
    placementZone: z.enum(["SAFE_CONTENT", "TRIM", "BLEED"]),
    physicalPageSide: bookPageSideSchema.nullable(),
    zIndex: z.number().int().min(-1_000).max(1_000),
    segments: z.array(bookPagePlacementSegmentV1Schema).min(1).max(2),
    appliedTokenApplications: z.array(bookPageTokenApplicationV1Schema).min(1).max(100),
    consumedDirectionFlags: z.array(bookPageDirectionFlagBindingV1Schema).max(100),
    resolvedVisualProperties: z.array(bookResolvedVisualPropertyV1Schema).min(1).max(200),
    renderPrimitive: bookResolvedRenderPrimitiveV1Schema,
    textLayoutPlan: z
        .object({
        textResource: bookProtectedTextResourceBindingV1Schema,
        fallbackChainId: opaqueIdSchema,
        preferredFontId: opaqueIdSchema,
        script: safeCodeSchema,
        languageTag: localeSchema,
        direction: z.enum(["LEFT_TO_RIGHT", "RIGHT_TO_LEFT", "TOP_TO_BOTTOM"]),
        requestedFontSizeMilliPoints: z.number().int().positive().max(1_000_000),
        minimumFontSizeMilliPoints: z.number().int().positive().max(1_000_000),
        lineHeightMilliPoints: z.number().int().positive().max(2_000_000),
        letterSpacingMilliPoints: coordinateSchema,
        textFlowPolicy: bookTextFlowPolicyV1Schema,
        codePointCount: z.number().int().positive().max(1_000_000),
        graphemeCount: z.number().int().positive().max(1_000_000),
        measuredWidthMilliPoints: z.number().int().positive().max(40_000_000),
        measuredHeightMilliPoints: z.number().int().positive().max(40_000_000),
        measuredLineCount: z.number().int().positive().max(100_000),
        expectedTextBounds: bookPageRectV1Schema,
        selectedFontIds: z.array(opaqueIdSchema).min(1).max(100),
        preflightMeasurementDigestSha256: sha256Schema,
    })
        .strict()
        .nullable(),
    visualOrderIndex: z.number().int().nonnegative().max(10_000),
    drawOrderIndex: z.number().int().nonnegative().max(10_000),
    accessibleOrderIndex: z.number().int().nonnegative().max(10_000).nullable(),
})
    .strict();
export const bookResolvedSurfaceBackgroundV1Schema = z
    .object({
    primitive: bookSurfaceBackgroundPrimitiveV1Schema,
    sourceTokenApplication: bookPageTokenApplicationV1Schema.nullable(),
    bounds: bookPageRectV1Schema,
    drawOrderIndex: z.literal(0),
})
    .strict();
export const bookMeaningfulSpatialMetricsV1Schema = z
    .object({
    policyBinding: bookProductionVersionBindingV1Schema,
    safeAreaCoverageMillionths: z.number().int().nonnegative().max(1_000_000),
    verticalSpanMillionths: z.number().int().nonnegative().max(1_000_000),
    leadingVoidMillionths: z.number().int().nonnegative().max(1_000_000),
    trailingVoidMillionths: z.number().int().nonnegative().max(1_000_000),
    meaningfulPlacementIds: z.array(opaqueIdSchema).max(500),
    metricsDigestSha256: sha256Schema,
})
    .strict()
    .superRefine((value, context) => unique(value.meaningfulPlacementIds, context, ["meaningfulPlacementIds"], "Meaningful spatial placements"));
export const bookCompositionPlanSurfaceV1Schema = z
    .object({
    surfaceId: opaqueIdSchema,
    surfaceMode: bookPageModeSchema,
    productPageRole: safeCodeSchema,
    surfaceDisposition: z.enum(["POPULATED", "INTENTIONAL_PRODUCTION_BLANK"]),
    intentionalBlankAuthorization: z
        .object({
        reason: z.enum(["CHAPTER_VERSO", "SIGNATURE_PADDING", "ENDPAPER", "VENDOR_REQUIRED"]),
        policyBinding: bookProductionVersionBindingV1Schema,
        policyDigestSha256: sha256Schema,
        evidenceId: opaqueIdSchema,
        authorizationDigestSha256: sha256Schema,
    })
        .strict()
        .nullable(),
    grammarId: opaqueIdSchema.nullable(),
    grammarSelection: z.enum(["ENTRY", "REQUESTED", "ALTERNATE"]).nullable(),
    surfaceBox: bookPageRectV1Schema,
    surfaceBackground: bookResolvedSurfaceBackgroundV1Schema,
    meaningfulSpatialMetrics: bookMeaningfulSpatialMetricsV1Schema.nullable(),
    spineSplitXMilliPoints: coordinateSchema.nullable(),
    pageFrames: z.array(bookPageFrameV1Schema).min(1).max(2),
    placements: z.array(bookPageResolvedPlacementV1Schema).max(500),
    drawOrderNodeIds: z.array(opaqueIdSchema).max(500),
    visualOrderNodeIds: z.array(opaqueIdSchema).max(500),
    accessibleOrderNodeIds: z.array(opaqueIdSchema).max(500),
})
    .strict()
    .superRefine((value, context) => {
    if (value.surfaceDisposition === "POPULATED" &&
        (value.grammarId === null ||
            value.grammarSelection === null ||
            value.intentionalBlankAuthorization !== null ||
            value.meaningfulSpatialMetrics === null ||
            value.placements.length === 0))
        context.addIssue({
            code: "custom",
            path: ["surfaceDisposition"],
            message: "A populated plan surface requires an exact grammar, metrics, and placed semantic content.",
        });
    if (value.surfaceDisposition === "INTENTIONAL_PRODUCTION_BLANK" &&
        (value.grammarId !== null ||
            value.grammarSelection !== null ||
            value.intentionalBlankAuthorization === null ||
            value.meaningfulSpatialMetrics !== null ||
            value.placements.length > 0 ||
            value.drawOrderNodeIds.length > 0 ||
            value.visualOrderNodeIds.length > 0 ||
            value.accessibleOrderNodeIds.length > 0 ||
            value.surfaceMode !== "PAGE"))
        context.addIssue({
            code: "custom",
            path: ["surfaceDisposition"],
            message: "An intentional blank plan surface must use PAGE topology, remain background-only, and cannot earn content, density, order, or differentiation credit.",
        });
    if (JSON.stringify(value.surfaceBackground.bounds) !== JSON.stringify(value.surfaceBox))
        context.addIssue({
            code: "custom",
            path: ["surfaceBackground", "bounds"],
            message: "Surface background must cover the exact full logical surface.",
        });
    unique(value.placements.map(({ placementId }) => placementId), context, ["placements"], "Placements");
    unique(value.placements.map(({ nodeId }) => nodeId), context, ["placements"], "Placed nodes");
    unique(value.pageFrames.map(({ pageId }) => pageId), context, ["pageFrames"], "Plan page frames");
    if (value.pageFrames.some((frame, index) => index > 0 &&
        frame.productionOrdinal !== value.pageFrames[index - 1].productionOrdinal + 1))
        context.addIssue({
            code: "custom",
            path: ["pageFrames"],
            message: "Physical page frames must remain in exact contiguous production-ordinal order.",
        });
    for (const [index, placement] of value.placements.entries()) {
        const segmentFrame = value.pageFrames.find(({ pageId }) => pageId === placement.segments[0]?.pageId);
        if ((value.surfaceMode === "PAGE" && placement.physicalPageSide !== null) ||
            (value.surfaceMode === "FACING_SPREAD" &&
                ((placement.segments.length === 2 && placement.physicalPageSide !== null) ||
                    (placement.segments.length === 1 &&
                        (placement.physicalPageSide === null ||
                            segmentFrame?.side !== placement.physicalPageSide)))))
            context.addIssue({
                code: "custom",
                path: ["placements", index, "physicalPageSide"],
                message: "PAGE placements and cross-spine spread placements own no side; every one-page spread placement must bind the exact side of its segment.",
            });
        unique(placement.implementationComponentIds, context, ["placements", index, "implementationComponentIds"], "Placement implementation components");
        unique(placement.segments.map(({ pageId }) => pageId), context, ["placements", index, "segments"], "Placement segment pages");
        unique(placement.appliedTokenApplications.map(({ token, targetProperty, componentId }) => `${token.tokenId}:${componentId}:${targetProperty}`), context, ["placements", index, "appliedTokenApplications"], "Applied token consumers");
        unique(placement.consumedDirectionFlags.map(({ path, componentId }) => `${path}:${componentId}`), context, ["placements", index, "consumedDirectionFlags"], "Consumed direction-flag consumers");
        unique(placement.resolvedVisualProperties.map(resolvedVisualPropertyScopeKey), context, ["placements", index, "resolvedVisualProperties"], "Resolved visual targets");
    }
    exactSet(value.drawOrderNodeIds, value.placements.map(({ nodeId }) => nodeId), context, ["drawOrderNodeIds"], "Draw node order");
    exactSet(value.visualOrderNodeIds, value.placements.map(({ nodeId }) => nodeId), context, ["visualOrderNodeIds"], "Visual node order");
    exactSet(value.accessibleOrderNodeIds, value.placements
        .filter(({ accessibleOrderIndex }) => accessibleOrderIndex !== null)
        .map(({ nodeId }) => nodeId), context, ["accessibleOrderNodeIds"], "Accessible node order");
    const expectedOrders = [
        ["drawOrderNodeIds", value.drawOrderNodeIds, "drawOrderIndex"],
        ["visualOrderNodeIds", value.visualOrderNodeIds, "visualOrderIndex"],
        ["accessibleOrderNodeIds", value.accessibleOrderNodeIds, "accessibleOrderIndex"],
    ];
    for (const [path, orderedIds, indexKey] of expectedOrders) {
        const observed = value.placements
            .filter((placement) => placement[indexKey] !== null)
            .sort((left, right) => Number(left[indexKey]) - Number(right[indexKey]));
        if (observed.some((placement, index) => placement[indexKey] !== index) ||
            observed.length !== orderedIds.length ||
            observed.some((placement, index) => placement.nodeId !== orderedIds[index]))
            context.addIssue({
                code: "custom",
                path: [path],
                message: `${path} must equal its exact unique contiguous placement indices.`,
            });
    }
    if ((value.surfaceMode === "PAGE" &&
        (value.pageFrames.length !== 1 || value.spineSplitXMilliPoints !== null)) ||
        (value.surfaceMode === "FACING_SPREAD" &&
            (value.pageFrames.length !== 2 || value.spineSplitXMilliPoints === null)))
        context.addIssue({
            code: "custom",
            path: ["pageFrames"],
            message: "Plan surface geometry must preserve the exact PAGE or FACING_SPREAD topology.",
        });
});
const compositionPlanBodySchema = z
    .object({
    contract: bookPageCompositionPlanContractSchema,
    planId: opaqueIdSchema,
    requestId: opaqueIdSchema,
    requestDigestSha256: sha256Schema,
    bookId: opaqueIdSchema,
    revisionId: opaqueIdSchema,
    product: bookProductionProductSchema,
    outputAudiencePolicy: bookOutputAudiencePolicyV1Schema,
    creationSourceBinding: bookGenerationSourceBindingV1Schema,
    runtimeSourceSnapshotId: opaqueIdSchema,
    runtimeSourceArtifact: bookProductionArtifactBindingV1Schema,
    creationBriefDigestSha256: sha256Schema,
    visualIntentDigestSha256: sha256Schema,
    directionKitId: opaqueIdSchema,
    directionKitDigestSha256: sha256Schema,
    directionKitSelectionDigestSha256: sha256Schema,
    visualImplementationPlanId: opaqueIdSchema,
    visualImplementationPlanDigestSha256: sha256Schema,
    sourceFingerprintDigestSha256: sha256Schema,
    realizedVisualSignatureDigestSha256: sha256Schema,
    visibleStyleSignatureDigestSha256: sha256Schema,
    visualDifferentiationGate: z.literal("FINGERPRINT_REALIZATION_ADAPTER_REQUIRED"),
    audienceAuthorityGate: z.literal("TRUSTED_IDENTITY_AND_CURRENT_CONSENT_ADAPTER_REQUIRED"),
    catalogId: opaqueIdSchema,
    catalogDigestSha256: sha256Schema,
    geometryId: opaqueIdSchema,
    geometryDigestSha256: sha256Schema,
    fontRegistryId: opaqueIdSchema,
    fontRegistryDigestSha256: sha256Schema,
    constraintObservations: z.array(bookPageConstraintObservationV1Schema).max(300),
    rendererBinding: bookProductionVersionBindingV1Schema,
    configurationBinding: bookProductionVersionBindingV1Schema,
    outputProfileBinding: bookProductionVersionBindingV1Schema,
    renderStageNodeId: opaqueIdSchema,
    stageGraphBinding: bookProductionVersionBindingV1Schema,
    productPolicyBinding: bookProductionVersionBindingV1Schema,
    renderExecutionMode: z.literal("DETERMINISTIC"),
    outputArtifactRole: safeCodeSchema,
    outputContentType: z.literal("application/pdf"),
    status: z.literal("COMPOSED"),
    surfaces: z.array(bookCompositionPlanSurfaceV1Schema).min(1).max(2_000),
    truthBoundary: z.literal("COMPOSED_AGAINST_SUPPLIED_TRUST_INPUTS"),
    assuranceScope: z.literal("NO_ARTIFACT_APPROVAL_RELEASE_VISUAL_QUALITY_PROVENANCE_OR_RENDER_EXECUTION_AUTHORITY"),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.constraintObservations.map(({ constraintId }) => constraintId), context, ["constraintObservations"], "Plan constraint observations");
    unique(value.surfaces.map(({ surfaceId }) => surfaceId), context, ["surfaces"], "Plan surfaces");
    unique(value.surfaces.flatMap(({ pageFrames }) => pageFrames.map(({ pageId }) => pageId)), context, ["surfaces"], "Global plan page IDs");
    unique(value.surfaces.flatMap(({ placements }) => placements.map(({ placementId }) => placementId)), context, ["surfaces"], "Global plan placement IDs");
    unique(value.surfaces.flatMap(({ placements }) => placements.map(({ nodeId }) => nodeId)), context, ["surfaces"], "Global plan node IDs");
    const orderedFrames = value.surfaces.flatMap(({ pageFrames }) => pageFrames);
    if (orderedFrames.some((frame, index) => frame.productionOrdinal !== index + 1))
        context.addIssue({
            code: "custom",
            path: ["surfaces"],
            message: "Plan surfaces and page frames must be stored in exact global production order with contiguous ordinals from one.",
        });
});
export const bookPageCompositionPlanV1Schema = compositionPlanBodySchema.safeExtend({
    planDigestSha256: sha256Schema,
});
const fontRunSchema = z
    .object({
    runId: opaqueIdSchema,
    fontId: opaqueIdSchema,
    fontAssetUseId: opaqueIdSchema,
    fontAssetUseDigestSha256: sha256Schema,
    graphemeStartInclusive: z.number().int().nonnegative().max(1_000_000),
    graphemeEndExclusive: z.number().int().positive().max(1_000_000),
    glyphStartInclusive: z.number().int().nonnegative().max(2_000_000),
    glyphEndExclusive: z.number().int().positive().max(2_000_000),
})
    .strict()
    .refine((run) => run.graphemeStartInclusive < run.graphemeEndExclusive &&
    run.glyphStartInclusive < run.glyphEndExclusive, "Font runs must be non-empty half-open ranges.");
export const bookDrawObservationV1Schema = z
    .object({
    drawId: opaqueIdSchema,
    placementId: opaqueIdSchema,
    nodeId: opaqueIdSchema,
    nodeDigestSha256: sha256Schema,
    slotId: opaqueIdSchema,
    observedImplementationComponentIds: z.array(opaqueIdSchema).min(1).max(100),
    zIndex: z.number().int().min(-1_000).max(1_000),
    visualOrderIndex: z.number().int().nonnegative().max(10_000),
    drawOrderIndex: z.number().int().nonnegative().max(10_000),
    accessibleOrderIndex: z.number().int().nonnegative().max(10_000).nullable(),
    drawStatus: z.literal("SUCCEEDED"),
    completedAt: timestampSchema,
    segments: z
        .array(z
        .object({
        pageId: opaqueIdSchema,
        destinationRectOnPage: bookPageRectV1Schema,
        sourceRectInPlacement: bookPageRectV1Schema,
        segmentOutputDigestSha256: sha256Schema,
        status: z.literal("SUCCEEDED"),
    })
        .strict())
        .min(1)
        .max(2),
    observedTokenApplications: z.array(bookPageTokenApplicationV1Schema).min(1).max(100),
    observedDirectionFlags: z.array(bookPageDirectionFlagBindingV1Schema).max(100),
    observedResolvedVisualProperties: z.array(bookResolvedVisualPropertyV1Schema).min(1).max(200),
    observedRenderPrimitive: bookResolvedRenderPrimitiveV1Schema,
    textObservation: z
        .object({
        rendererInputMacSha256: sha256Schema,
        normalizedRendererInputMacSha256: sha256Schema,
        normalizationPolicy: z.literal("NFC"),
        direction: z.enum(["LEFT_TO_RIGHT", "RIGHT_TO_LEFT", "TOP_TO_BOTTOM"]),
        preferredFontId: opaqueIdSchema,
        letterSpacingMilliPoints: coordinateSchema,
        measurementEngineBinding: bookProductionVersionBindingV1Schema,
        measurementConfigDigestSha256: sha256Schema,
        segmentationEngineBinding: bookProductionVersionBindingV1Schema,
        segmentationConfigDigestSha256: sha256Schema,
        bidiEngineBinding: bookProductionVersionBindingV1Schema,
        bidiConfigDigestSha256: sha256Schema,
        shapingEngineBinding: bookProductionVersionBindingV1Schema.nullable(),
        shapedGlyphStreamMacSha256: sha256Schema,
        codePointCount: z.number().int().positive().max(1_000_000),
        graphemeCount: z.number().int().positive().max(1_000_000),
        glyphCount: z.number().int().positive().max(2_000_000),
        replacementGlyphCount: z.literal(0),
        renderedFontSizeMilliPoints: z.number().int().positive().max(1_000_000),
        renderedLineHeightMilliPoints: z.number().int().positive().max(2_000_000),
        renderedLineCount: z.number().int().positive().max(100_000),
        observedTextFlowPolicy: bookTextFlowPolicyV1Schema,
        actualBounds: bookPageRectV1Schema,
        fontRuns: z.array(fontRunSchema).min(1).max(10_000),
        observedAttributionChannelBindings: z.array(attributionChannelBindingSchema).max(4),
    })
        .strict()
        .nullable(),
    visibleOutputDigestSha256: sha256Schema,
})
    .strict()
    .superRefine((value, context) => {
    unique(value.observedImplementationComponentIds, context, ["observedImplementationComponentIds"], "Observed implementation components");
    unique(value.segments.map(({ pageId }) => pageId), context, ["segments"], "Draw segment page IDs");
    unique(value.observedTokenApplications.map(({ token, targetProperty, componentId }) => `${token.tokenId}:${componentId}:${targetProperty}`), context, ["observedTokenApplications"], "Observed token consumers");
    unique(value.observedDirectionFlags.map(({ path, componentId }) => `${path}:${componentId}`), context, ["observedDirectionFlags"], "Observed direction-flag consumers");
    unique(value.observedResolvedVisualProperties.map(resolvedVisualPropertyScopeKey), context, ["observedResolvedVisualProperties"], "Observed resolved visual targets");
    if (value.textObservation)
        unique(value.textObservation.fontRuns.map(({ runId }) => runId), context, ["textObservation", "fontRuns"], "Observed font-run IDs");
    if (value.textObservation) {
        const runs = value.textObservation.fontRuns;
        if (runs[0]?.graphemeStartInclusive !== 0 ||
            runs[0]?.glyphStartInclusive !== 0 ||
            runs.some((run, index) => index > 0 &&
                (run.graphemeStartInclusive !== runs[index - 1].graphemeEndExclusive ||
                    run.glyphStartInclusive !== runs[index - 1].glyphEndExclusive)) ||
            runs.at(-1)?.graphemeEndExclusive !== value.textObservation.graphemeCount ||
            runs.at(-1)?.glyphEndExclusive !== value.textObservation.glyphCount)
            context.addIssue({
                code: "custom",
                path: ["textObservation", "fontRuns"],
                message: "Observed font runs must be stored as exact gapless grapheme and glyph partitions from zero through the reported totals.",
            });
    }
});
export const bookSurfaceBackgroundObservationV1Schema = z
    .object({
    observedSurfaceBackground: bookResolvedSurfaceBackgroundV1Schema,
    drawStatus: z.literal("SUCCEEDED"),
    completedAt: timestampSchema,
    segments: z
        .array(z
        .object({
        pageId: opaqueIdSchema,
        sourceRectInSurface: bookPageRectV1Schema,
        destinationRectOnPage: bookPageRectV1Schema,
        status: z.literal("SUCCEEDED"),
        segmentOutputDigestSha256: sha256Schema,
    })
        .strict())
        .min(1)
        .max(2),
    backgroundOutputDigestSha256: sha256Schema,
})
    .strict()
    .superRefine((value, context) => unique(value.segments.map(({ pageId }) => pageId), context, ["segments"], "Background segment page IDs"));
export const bookDrawLedgerSurfaceV1Schema = z
    .object({
    surfaceId: opaqueIdSchema,
    surfaceMode: bookPageModeSchema,
    surfaceDisposition: z.enum(["POPULATED", "INTENTIONAL_PRODUCTION_BLANK"]),
    intentionalBlankAuthorization: z
        .object({
        reason: z.enum(["CHAPTER_VERSO", "SIGNATURE_PADDING", "ENDPAPER", "VENDOR_REQUIRED"]),
        policyBinding: bookProductionVersionBindingV1Schema,
        policyDigestSha256: sha256Schema,
        evidenceId: opaqueIdSchema,
        authorizationDigestSha256: sha256Schema,
    })
        .strict()
        .nullable(),
    grammarId: opaqueIdSchema.nullable(),
    physicalPageOutputs: z
        .array(z
        .object({
        pageId: opaqueIdSchema,
        productionOrdinal: z.number().int().positive().max(2_000),
        side: bookPageSideSchema,
        outputPageDigestSha256: sha256Schema,
    })
        .strict())
        .min(1)
        .max(2),
    observedSurfaceBackground: bookSurfaceBackgroundObservationV1Schema,
    visualOrderNodeIds: z.array(opaqueIdSchema).max(500),
    drawOrderNodeIds: z.array(opaqueIdSchema).max(500),
    accessibleOrderNodeIds: z.array(opaqueIdSchema).max(500),
    draws: z.array(bookDrawObservationV1Schema).max(500),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.physicalPageOutputs.map(({ pageId }) => pageId), context, ["physicalPageOutputs"], "Ledger physical-page IDs");
    unique(value.draws.map(({ drawId }) => drawId), context, ["draws"], "Surface draw IDs");
    unique(value.draws.map(({ placementId }) => placementId), context, ["draws"], "Surface drawn placements");
    unique(value.draws.map(({ nodeId }) => nodeId), context, ["draws"], "Surface drawn nodes");
    if (value.observedSurfaceBackground.segments.length !== value.physicalPageOutputs.length ||
        value.observedSurfaceBackground.segments.some(({ pageId }, index) => pageId !== value.physicalPageOutputs[index]?.pageId))
        context.addIssue({
            code: "custom",
            path: ["observedSurfaceBackground", "segments"],
            message: "Background segments must exactly cover the canonical physical-page output order.",
        });
    const physicalPageIds = new Set(value.physicalPageOutputs.map(({ pageId }) => pageId));
    if (value.draws.some((draw) => draw.segments.some(({ pageId }) => !physicalPageIds.has(pageId))))
        context.addIssue({
            code: "custom",
            path: ["draws"],
            message: "Every draw segment must target an exact physical page on its surface.",
        });
    if (value.physicalPageOutputs.some((page, index) => index > 0 &&
        page.productionOrdinal !== value.physicalPageOutputs[index - 1].productionOrdinal + 1))
        context.addIssue({
            code: "custom",
            path: ["physicalPageOutputs"],
            message: "Ledger physical pages must remain in contiguous production order.",
        });
    if (value.surfaceDisposition === "POPULATED" &&
        (value.grammarId === null ||
            value.intentionalBlankAuthorization !== null ||
            value.draws.length === 0))
        context.addIssue({
            code: "custom",
            path: ["surfaceDisposition"],
            message: "A populated ledger surface requires its exact grammar and successful draws.",
        });
    if (value.surfaceDisposition === "INTENTIONAL_PRODUCTION_BLANK" &&
        (value.grammarId !== null ||
            value.intentionalBlankAuthorization === null ||
            value.surfaceMode !== "PAGE" ||
            value.draws.length > 0 ||
            value.visualOrderNodeIds.length > 0 ||
            value.drawOrderNodeIds.length > 0 ||
            value.accessibleOrderNodeIds.length > 0))
        context.addIssue({
            code: "custom",
            path: ["surfaceDisposition"],
            message: "An intentional production blank must remain a background-only ledger surface with no grammar, placements, or orders.",
        });
    if ((value.surfaceMode === "PAGE" && value.physicalPageOutputs.length !== 1) ||
        (value.surfaceMode === "FACING_SPREAD" && value.physicalPageOutputs.length !== 2))
        context.addIssue({
            code: "custom",
            path: ["physicalPageOutputs"],
            message: "Ledger physical-page cardinality must preserve exact PAGE or FACING_SPREAD topology.",
        });
    const expectedOrders = [
        ["drawOrderNodeIds", value.drawOrderNodeIds, "drawOrderIndex"],
        ["visualOrderNodeIds", value.visualOrderNodeIds, "visualOrderIndex"],
        ["accessibleOrderNodeIds", value.accessibleOrderNodeIds, "accessibleOrderIndex"],
    ];
    for (const [path, orderedIds, indexKey] of expectedOrders) {
        const observed = value.draws
            .filter((draw) => draw[indexKey] !== null)
            .sort((left, right) => Number(left[indexKey]) - Number(right[indexKey]));
        if (observed.some((draw, index) => draw[indexKey] !== index) ||
            observed.length !== orderedIds.length ||
            observed.some((draw, index) => draw.nodeId !== orderedIds[index]))
            context.addIssue({
                code: "custom",
                path: [path],
                message: `${path} must equal the exact unique contiguous draw indices.`,
            });
    }
    if (value.draws.some((draw, index) => draw.drawOrderIndex !== index))
        context.addIssue({
            code: "custom",
            path: ["draws"],
            message: "Ledger draws must be stored in their exact canonical contiguous draw order.",
        });
});
const drawLedgerBodySchema = z
    .object({
    contract: bookDrawLedgerContractSchema,
    ledgerId: opaqueIdSchema,
    planId: opaqueIdSchema,
    planDigestSha256: sha256Schema,
    requestId: opaqueIdSchema,
    requestDigestSha256: sha256Schema,
    bookId: opaqueIdSchema,
    revisionId: opaqueIdSchema,
    product: bookProductionProductSchema,
    outputAudiencePolicy: bookOutputAudiencePolicyV1Schema,
    creationSourceBinding: bookGenerationSourceBindingV1Schema,
    runtimeSourceSnapshotId: opaqueIdSchema,
    runtimeSourceArtifact: bookProductionArtifactBindingV1Schema,
    creationBriefDigestSha256: sha256Schema,
    visualIntentDigestSha256: sha256Schema,
    directionKitId: opaqueIdSchema,
    directionKitDigestSha256: sha256Schema,
    directionKitSelectionDigestSha256: sha256Schema,
    visualImplementationPlanId: opaqueIdSchema,
    visualImplementationPlanDigestSha256: sha256Schema,
    sourceFingerprintDigestSha256: sha256Schema,
    realizedVisualSignatureDigestSha256: sha256Schema,
    visibleStyleSignatureDigestSha256: sha256Schema,
    visualDifferentiationGate: z.literal("FINGERPRINT_REALIZATION_ADAPTER_REQUIRED"),
    audienceAuthorityGate: z.literal("TRUSTED_IDENTITY_AND_CURRENT_CONSENT_ADAPTER_REQUIRED"),
    compositionPlanAuditGate: z.literal("EXACT_COMPOSITION_PLAN_AUDIT_EVIDENCE_REQUIRED"),
    commitReceiptAuthorityGate: z.literal("TARGET_BOUND_COMMIT_RECEIPT_REQUIRED"),
    unreportedPaintGate: z.literal("TRUSTED_EXHAUSTIVE_RENDER_COMMAND_INSTRUMENTATION_REQUIRED"),
    pdfPixelAuthorityGate: z.literal("M2E_OR_TRUSTED_PDF_RASTER_ATTESTATION_REQUIRED"),
    catalogId: opaqueIdSchema,
    catalogDigestSha256: sha256Schema,
    constraintObservations: z.array(bookPageConstraintObservationV1Schema).max(300),
    rendererBinding: bookProductionVersionBindingV1Schema,
    configurationBinding: bookProductionVersionBindingV1Schema,
    outputProfileBinding: bookProductionVersionBindingV1Schema,
    stageGraphBinding: bookProductionVersionBindingV1Schema,
    productPolicyBinding: bookProductionVersionBindingV1Schema,
    renderExecutionMode: z.literal("DETERMINISTIC"),
    outputArtifactRole: safeCodeSchema,
    outputContentType: z.literal("application/pdf"),
    executionInputDigestSha256: sha256Schema,
    executionInputObservedAt: timestampSchema,
    runtimeIdentityDigestSha256: sha256Schema,
    runtimeDefinitionDigestSha256: sha256Schema,
    runtimeRevision: z.number().int().positive(),
    stageNodeId: opaqueIdSchema,
    attemptId: opaqueIdSchema,
    attemptTerminalAt: timestampSchema,
    resultId: opaqueIdSchema,
    resultDigestSha256: sha256Schema,
    selectedAt: timestampSchema,
    selectionDigestSha256: sha256Schema,
    artifactCommitId: opaqueIdSchema,
    artifactSetDigestSha256: sha256Schema,
    compositionInputManifestArtifact: bookProductionArtifactBindingV1Schema,
    renderObservationManifestArtifact: bookProductionArtifactBindingV1Schema,
    pageAssemblyManifestDigestSha256: sha256Schema,
    commitMutationId: opaqueIdSchema,
    commitExpectedRevision: z.number().int().nonnegative(),
    commitResultRevision: z.number().int().positive(),
    commitRecordedAt: timestampSchema,
    outputArtifact: bookProductionArtifactBindingV1Schema,
    executionStartedAt: timestampSchema,
    executionCompletedAt: timestampSchema,
    artifactCommittedAt: timestampSchema,
    observedAt: timestampSchema,
    geometryId: opaqueIdSchema,
    geometryDigestSha256: sha256Schema,
    fontRegistryId: opaqueIdSchema,
    fontRegistryDigestSha256: sha256Schema,
    surfaces: z.array(bookDrawLedgerSurfaceV1Schema).min(1).max(2_000),
    truthBoundary: z.literal("SUPPLIED_DRAW_OBSERVATIONS_NOT_AUTHENTICATED"),
    assuranceScope: z.literal("NO_ARTIFACT_APPROVAL_RELEASE_VISUAL_QUALITY_PROVENANCE_OR_EXECUTION_AUTHORITY"),
})
    .strict()
    .superRefine((value, context) => {
    unique(value.surfaces.map(({ surfaceId }) => surfaceId), context, ["surfaces"], "Ledger surfaces");
    unique(value.constraintObservations.map(({ constraintId }) => constraintId), context, ["constraintObservations"], "Ledger constraint observations");
    unique(value.surfaces.flatMap(({ physicalPageOutputs }) => physicalPageOutputs.map(({ pageId }) => pageId)), context, ["surfaces"], "Global ledger physical-page IDs");
    unique(value.surfaces.flatMap(({ draws }) => draws.map(({ drawId }) => drawId)), context, ["surfaces"], "Global draw IDs");
    unique(value.surfaces.flatMap(({ draws }) => draws.map(({ placementId }) => placementId)), context, ["surfaces"], "Globally drawn placements");
    unique([
        value.runtimeSourceArtifact.artifactId,
        value.compositionInputManifestArtifact.artifactId,
        value.renderObservationManifestArtifact.artifactId,
        value.outputArtifact.artifactId,
    ], context, ["compositionInputManifestArtifact"], "Ledger input-manifest, observation-manifest, and output artifact IDs");
    const orderedPages = value.surfaces.flatMap(({ physicalPageOutputs }) => physicalPageOutputs);
    if (orderedPages.some((page, index) => page.productionOrdinal !== index + 1))
        context.addIssue({
            code: "custom",
            path: ["surfaces"],
            message: "Ledger surfaces and physical pages must be stored in exact canonical production order from one.",
        });
    const executionStarted = Date.parse(value.executionStartedAt);
    const executionCompleted = Date.parse(value.executionCompletedAt);
    const artifactCommitted = Date.parse(value.artifactCommittedAt);
    const selected = Date.parse(value.selectedAt);
    const observed = Date.parse(value.observedAt);
    const inputObserved = Date.parse(value.executionInputObservedAt);
    if (!(executionStarted < executionCompleted) ||
        !(executionCompleted < artifactCommitted) ||
        artifactCommitted !== selected ||
        !(artifactCommitted < observed) ||
        inputObserved <= executionCompleted ||
        inputObserved >= artifactCommitted ||
        inputObserved > observed ||
        Date.parse(value.commitRecordedAt) !== artifactCommitted ||
        Date.parse(value.attemptTerminalAt) !== artifactCommitted)
        context.addIssue({
            code: "custom",
            path: ["executionStartedAt"],
            message: "Ledger execution, supplied input observation, commit, selection, and final observation timestamps must form the exact causal window.",
        });
    for (const [surfaceIndex, surface] of value.surfaces.entries()) {
        const backgroundCompleted = Date.parse(surface.observedSurfaceBackground.completedAt);
        if (!(executionStarted < backgroundCompleted && backgroundCompleted <= executionCompleted))
            context.addIssue({
                code: "custom",
                path: ["surfaces", surfaceIndex, "observedSurfaceBackground", "completedAt"],
                message: "Background observation must complete inside the execution interval.",
            });
        for (const [drawIndex, draw] of surface.draws.entries()) {
            const drawCompleted = Date.parse(draw.completedAt);
            if (!(executionStarted < drawCompleted && drawCompleted <= executionCompleted))
                context.addIssue({
                    code: "custom",
                    path: ["surfaces", surfaceIndex, "draws", drawIndex, "completedAt"],
                    message: "Every draw observation must complete inside the execution interval.",
                });
        }
    }
    if (value.commitResultRevision !== value.commitExpectedRevision + 1 ||
        value.commitResultRevision > value.runtimeRevision)
        context.addIssue({
            code: "custom",
            path: ["commitResultRevision"],
            message: "Commit receipt revisions must describe one valid runtime transition.",
        });
    if (value.outputArtifact.role !== value.outputArtifactRole ||
        value.outputArtifact.contentType !== value.outputContentType ||
        value.outputArtifact.pageCount !== orderedPages.length)
        context.addIssue({
            code: "custom",
            path: ["outputArtifact"],
            message: "The exact output binding must match the planned role, PDF content type, and canonical physical-page count.",
        });
    if (value.compositionInputManifestArtifact.role !== "COMPOSITION_INPUT_MANIFEST" ||
        value.compositionInputManifestArtifact.contentType !== "application/json" ||
        value.compositionInputManifestArtifact.checksumSha256 !== value.executionInputDigestSha256 ||
        value.renderObservationManifestArtifact.role !== "RENDER_OBSERVATION_MANIFEST" ||
        value.renderObservationManifestArtifact.contentType !== "application/json" ||
        value.renderObservationManifestArtifact.checksumSha256 !==
            value.pageAssemblyManifestDigestSha256)
        context.addIssue({
            code: "custom",
            path: ["compositionInputManifestArtifact"],
            message: "The immutable composition-input and render-observation manifests must bind the exact canonical execution and page-assembly digests.",
        });
});
export const bookDrawLedgerV1Schema = drawLedgerBodySchema.safeExtend({
    ledgerDigestSha256: sha256Schema,
});
//# sourceMappingURL=book-page-composition.js.map